
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { getFirebaseAuth, initializeFirebase } from './services/firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, setPersistence, browserLocalPersistence, browserSessionPersistence, deleteUser, User, signInAnonymously, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { database, subscribeToPath } from './services/database';
import { isEqual } from 'lodash-es';
import type { Department, StaffMember, Shift, Schedule, View, LoginCredentials, AdminUser, Notification, AssignmentStatus, ScheduleEntry, TimeOffRequest, TimeOffRequestStatus, Theme, AvailabilityStatus, DayOfWeek, PickupLocation, ReservationSource, ShuttleReservation, ParkingReservation, AppPage, AttendanceRecord, AttendanceRequest, AttendanceArchive, WeeklyMetrics, DailyMetrics, WorkloadFactors, MaintenanceRequest, DailyNightAuditReport } from './types';
import { ALL_DAYS, ALL_PAGES, DAY_INDEX_MAP } from './types';
import { subscribeToNotifications, markNotificationAsRead, markAllNotificationsAsRead, createNotification, clearNotifications } from './services/notificationService';
import { formatDateRange, getWeekStartDate, getWeekDates, formatDateYYYYMMDD, getDayOfWeekFromDate, calculateHours } from './utils/dateUtils';
import { Icons } from './components/ui/Icons';

import Login from './components/auth/Login';
import Header from './components/layout/Header';
import SchedulerView from './components/scheduler/SchedulerView';
import SchedulerPreviewView from './components/scheduler/SchedulerPreviewView';
import StaffManagement from './components/staff/StaffManagement';
import SettingsView from './components/settings/SettingsView';
import StaffPortal from './components/staff_portal/StaffPortal';
import AdminProfileView from './components/admin/AdminProfileView';
import LoadingSpinner from './components/layout/LoadingSpinner';
import PermissionErrorMessage from './components/auth/PermissionErrorMessage';
import TimeOffManagementView from './components/admin/TimeOffManagementView';
import PrintPage from './components/scheduler/PrintPage';
import type { PrintData } from './components/scheduler/PrintPage';
import ShuttlePrintPage from './components/shuttle/ShuttlePrintPage';
import type { ShuttlePrintData } from './components/shuttle/ShuttlePrintPage';
import ParkingPrintPage from './components/parking/ParkingPrintPage';
import type { ParkingPrintData } from './components/parking/ParkingPrintPage';
import BulkParkingPrintPage from './components/parking/BulkParkingPrintPage';
import type { BulkParkingPrintData } from './components/parking/BulkParkingPrintPage';
import WorkforceTracker from './components/search/WorkforceTracker';
import ShuttleReservationView from './components/shuttle/ShuttleReservationView';
import ParkingReservationView from './components/parking/ParkingReservationView';
import HelpCenterView from './components/help/HelpCenterView';
import ReportsView from './components/reports/ReportsView';
import AttendanceView from './components/AttendanceView';
import AttendancePrintPage from './components/AttendancePrintPage';
import PayrollReportPrintPage from './components/PayrollReportPrintPage';
import StaffWorkingHoursPrintPage from './components/StaffWorkingHoursPrintPage';
import TaskManagerView from './components/tasks/TaskManagerView';
import MemosView from './components/memos/MemosView';
import MaintenanceView from './components/maintenance/MaintenanceView';
import AccessManagerView from './components/access/AccessManagerView';

import { handleDatabaseError, OperationType } from './services/databaseErrorHandler';
import type { DatabaseErrorInfo } from './services/databaseErrorHandler';

const App: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const [loggedInStaff, setLoggedInStaff] = useState<StaffMember | null>(null);
    const [isFirebaseInitialized, setIsFirebaseInitialized] = useState(false);
    const [isAuthLoaded, setIsAuthLoaded] = useState(false);
    const [isAdminsLoaded, setIsAdminsLoaded] = useState(false);
    const lastSavedDataRef = useRef<any>(null);
    const lastLoadedDataRef = useRef<any>(null);
    const [firebaseInitError, setFirebaseInitError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDataLoaded, setIsDataLoaded] = useState<{
        schedule: boolean;
        departments: boolean;
        shifts: boolean;
        staffList: boolean;
        settings: boolean;
    }>({
        schedule: false,
        departments: false,
        shifts: false,
        staffList: false,
        settings: false
    });
    const [isSaving, setIsSaving] = useState(false);
    const [firebasePermissionError, setFirebasePermissionError] = useState(false);
    
    const [theme, setTheme] = useState<Theme>(() => (localStorage.getItem('theme') as Theme) || 'system');
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme') as Theme | null;
        if (savedTheme === 'dark') return true;
        if (savedTheme === 'system' || !savedTheme) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches;
        }
        return false;
    });

    const [departments, setDepartments] = useState<Department[]>([]);
    const [staffList, setStaffList] = useState<StaffMember[]>([]);
    const [shifts, setShifts] = useState<Shift[]>([]);
    const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
    const [reservationSources, setReservationSources] = useState<ReservationSource[]>([]);
    const [shuttleReservations, setShuttleReservations] = useState<ShuttleReservation[]>([]);
    const [parkingReservations, setParkingReservations] = useState<ParkingReservation[]>([]);
    const [archivedParkingReservations, setArchivedParkingReservations] = useState<ParkingReservation[]>([]);
    const [weekStartDay, setWeekStartDay] = useState<DayOfWeek>('Monday');
    const [schedule, setSchedule] = useState<Schedule>({});
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const notificationsRef = useRef<Notification[]>([]);
    const processedReservationsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        notificationsRef.current = notifications;
    }, [notifications]);
    const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[]>([]);
    const [printData, setPrintData] = useState<PrintData | null>(null);
    const [shuttlePrintData, setShuttlePrintData] = useState<ShuttlePrintData | null>(null);
    const [parkingPrintData, setParkingPrintData] = useState<ParkingPrintData | null>(null);
    const [bulkParkingPrintData, setBulkParkingPrintData] = useState<BulkParkingPrintData | null>(null);
    const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
    const [attendanceRequests, setAttendanceRequests] = useState<AttendanceRequest[]>([]);
    const [attendanceArchives, setAttendanceArchives] = useState<AttendanceArchive[]>([]);
    const [staffWorkingHoursPrintData, setStaffWorkingHoursPrintData] = useState<any | null>(null);
    const [attendancePrintData, setAttendancePrintData] = useState<{
        periodStart: string;
        periodEnd: string;
        records: any[];
        staffList: StaffMember[];
    } | null>(null);
    const [payrollReportPrintData, setPayrollReportPrintData] = useState<{
        periodStart: string;
        periodEnd: string;
        records: AttendanceRecord[];
        attendanceRequests?: AttendanceRequest[];
        staffList: StaffMember[];
        shifts: Shift[];
        propertyName: string;
    } | null>(null);
    const [propertyName, setPropertyName] = useState<string>('Hotel Operations Hub');

    const [parkingCapacity, setParkingCapacity] = useState<number>(100);
    const [showLaborFactors, setShowLaborFactors] = useState<boolean>(true);
    const [workloadFactors, setWorkloadFactors] = useState<WorkloadFactors>({
        arrivalTime: 6,
        departureTime: 3,
        stayoverTime: 1,
        bufferMultiplier: 1.2,
        workloadFactor: 0.30
    });
    const [weeklyMetrics, setWeeklyMetrics] = useState<WeeklyMetrics>({});
    const [maintenanceRequests, setMaintenanceRequests] = useState<MaintenanceRequest[]>([]);
    const [nightAuditReports, setNightAuditReports] = useState<DailyNightAuditReport[]>([]);

    const [view, setView] = useState<View>({ page: 'scheduler' });
    const [selectedDate, setSelectedDate] = useState<string>(() => {
        const now = new Date();
        const hour = now.getHours();
        if (hour >= 14) {
            const tomorrow = new Date(now);
            tomorrow.setDate(now.getDate() + 1);
            return formatDateYYYYMMDD(tomorrow);
        }
        return formatDateYYYYMMDD(now);
    });

    const currentAdminUser = useMemo(() => {
        if (!user) return null;
        return adminUsers.find(a => a.uid === user.uid) || null;
    }, [user, adminUsers]);

    const isSuperAdmin = useMemo(() => {
        const role = currentAdminUser?.role?.toLowerCase();
        return role === 'super admin' || role === 'admin' || role === 'administrator';
    }, [currentAdminUser]);

    const allowedPages = useMemo(() => {
        // If not logged in as an admin in the database, return restricted pages
        if (!currentAdminUser) return ['myProfile', 'help'] as AppPage[];
        
        // Use their defined pages or default to all pages if not set
        let pages = currentAdminUser.allowedPages || ALL_PAGES;
        
        // If they don't have 'scheduler' access, automatically grant 'schedulerPreview'
        if (!pages.includes('scheduler') && !pages.includes('schedulerPreview')) {
            pages = [...pages, 'schedulerPreview'];
        }
        
        // Ensure myProfile and help are always available to prevent lockouts
        const essentialPages: AppPage[] = ['myProfile', 'help'];
        const combined = [...new Set([...pages, ...essentialPages])];
        
        return combined as AppPage[];
    }, [currentAdminUser]);

    const handleSetView = useCallback((newView: View) => {
        let pageToSet = newView.page;
        
        // Redirect scheduler to schedulerPreview if scheduler is not allowed but preview is
        if (pageToSet === 'scheduler' && !allowedPages.includes('scheduler') && allowedPages.includes('schedulerPreview')) {
            pageToSet = 'schedulerPreview';
        }

        if (user && !allowedPages.includes(pageToSet)) {
            toast.error(`You do not have permission to access the ${pageToSet} page.`);
            return;
        }
        setView({ ...newView, page: pageToSet });
    }, [user, allowedPages]);

    const defaultAvailability = useMemo(() => ALL_DAYS.reduce((acc, day) => {
        acc[day] = 'Available';
        return acc;
    }, {} as { [key in DayOfWeek]: AvailabilityStatus }), []);

     useEffect(() => {
        // Register the service worker for PWA functionality
        if ('serviceWorker' in navigator) {
            window.addEventListener('load', () => {
                navigator.serviceWorker.register('/service-worker.js')
                    .then(registration => {
                        console.log('ServiceWorker registration successful with scope: ', registration.scope);
                    })
                    .catch(err => {
                        console.error('ServiceWorker registration failed: ', err);
                    });
            });
        }

        const init = async () => {
            try {
                await initializeFirebase();
                setIsFirebaseInitialized(true);
            } catch (error: any) {
                console.error("Firebase initialization failed:", error);
                setFirebaseInitError(error.message || "Failed to initialize system.");
                toast.error("Failed to initialize system. Please check your configuration.");
                setIsLoading(false);
            }
        };
        init();
      }, []);

     useEffect(() => {
        const root = window.document.documentElement;
        const body = window.document.body;
        const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
        
        setIsDarkMode(isDark);
        
        // Apply to both html and body for maximum compatibility
        root.classList.remove('light', 'dark');
        root.classList.add(isDark ? 'dark' : 'light');
        body.classList.remove('light', 'dark');
        body.classList.add(isDark ? 'dark' : 'light');
        
        localStorage.setItem('theme', theme);

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            if (theme === 'system') {
                const newIsDark = mediaQuery.matches;
                setIsDarkMode(newIsDark);
                root.classList.remove('light', 'dark');
                root.classList.add(newIsDark ? 'dark' : 'light');
                body.classList.remove('light', 'dark');
                body.classList.add(newIsDark ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, [theme]);


    const cleanObject = (obj: any): any => {
        if (obj === null || obj === undefined) return undefined;
        if (obj instanceof Date) return obj.toISOString();
        if (Array.isArray(obj)) {
            return obj
                .map(v => cleanObject(v))
                .filter(v => v !== undefined);
        }
        if (typeof obj === 'object') {
            const result: any = {};
            Object.keys(obj).forEach(key => {
                const value = cleanObject(obj[key]);
                if (value !== undefined) {
                    result[key] = value;
                }
            });
            return result;
        }
        return obj;
    };

    const saveData = useCallback(async (data: { departments: Department[], staffList: StaffMember[], shifts: Shift[], schedule: Schedule, pickupLocations: PickupLocation[], reservationSources: ReservationSource[], shuttleReservations: ShuttleReservation[], parkingReservations: ParkingReservation[], archivedParkingReservations: ParkingReservation[], weekStartDay: DayOfWeek, propertyName: string, parkingCapacity: number, workloadFactors: WorkloadFactors }) => {
        if (!user || !isFirebaseInitialized) return;
        
        // Convert arrays to objects with IDs as keys for better RTDB structure
        const arrayToMap = (arr: any[]) => {
            const map: any = {};
            arr.forEach(item => {
                if (item.id) map[item.id] = item;
            });
            return map;
        };

        const dataToSave = {
            ...data,
            departments: arrayToMap(data.departments),
            staffList: arrayToMap(data.staffList),
            shifts: arrayToMap(data.shifts),
            pickupLocations: arrayToMap(data.pickupLocations),
            reservationSources: arrayToMap(data.reservationSources),
            shuttleReservations: arrayToMap(data.shuttleReservations),
            parkingReservations: arrayToMap(data.parkingReservations),
            archivedParkingReservations: arrayToMap(data.archivedParkingReservations)
        };

        const cleanedData = cleanObject(dataToSave);
        
        // Don't save if data is identical to what we last saved or loaded
        if (lastSavedDataRef.current && isEqual(lastSavedDataRef.current, cleanedData)) {
            console.log("Skipping save: data identical to last saved");
            return;
        }
        if (lastLoadedDataRef.current && isEqual(lastLoadedDataRef.current, cleanedData)) {
            console.log("Skipping save: data identical to last loaded");
            return;
        }

        setIsSaving(true);
        console.log("Saving schedule data to Database...", cleanedData);
        try {
            await database.saveScheduleData(cleanedData);
            lastSavedDataRef.current = JSON.parse(JSON.stringify(cleanedData)); // Store a deep copy
            setFirebasePermissionError(false);
            console.log("Schedule data saved successfully");
        } catch (error: any) {
            console.error("Database write error:", error);
            if (error.code === 'PERMISSION_DENIED' || error.message?.toLowerCase().includes('permission denied')) {
                if (user) setFirebasePermissionError(true);
                toast.error('Permission denied: You do not have permission to save changes.');
            } else {
                toast.error('Failed to save data. Check your connection.');
            }
        } finally {
            setTimeout(() => setIsSaving(false), 500);
        }
    }, [user, isFirebaseInitialized]);

    // Compute overall loading state
    useEffect(() => {
        if (isDataLoaded.schedule && isDataLoaded.departments && isDataLoaded.shifts && isDataLoaded.staffList && isDataLoaded.settings) {
            setIsLoading(false);
        }
    }, [isDataLoaded]);

    useEffect(() => {
        // Don't save on initial data load or if not logged in.
        if (isLoading || isSaving || !user || !isFirebaseInitialized || !isAdminsLoaded) return;
        
        // Safety check: Don't save if all core lists are empty (highly likely a race condition or sync error)
        if (Object.keys(schedule).length === 0 && departments.length === 0 && staffList.length === 0) {
            console.warn("Save blocked: All core data lists are empty. This might be a race condition.");
            return;
        }

        // Only Super Admins should save the global schedule data
        const currentAdminUser = adminUsers.find(a => a.uid === user.uid);
        const isAdmin = adminUsers.some(a => a.uid === user.uid && (a.role === 'Super Admin' || a.role === 'Admin' || a.role === 'Administrator' || a.role === 'Manager' || (a as any).type === 'admin'));
        const isSuperAdmin = currentAdminUser?.role?.toLowerCase() === 'super admin' || currentAdminUser?.role?.toLowerCase() === 'admin' || currentAdminUser?.role?.toLowerCase() === 'administrator' || currentAdminUser?.role?.toLowerCase() === 'manager';
        if (!isAdmin) return;
    
        const currentData = { departments, staffList, shifts, schedule, pickupLocations, reservationSources, shuttleReservations, parkingReservations, archivedParkingReservations, weekStartDay, propertyName, parkingCapacity, workloadFactors };
        const cleanedCurrentData = cleanObject(currentData);
        
        // Only trigger save if data has actually changed from what we last saved or loaded
        if (lastSavedDataRef.current && isEqual(lastSavedDataRef.current, cleanedCurrentData)) return;
        if (lastLoadedDataRef.current && isEqual(lastLoadedDataRef.current, cleanedCurrentData)) return;

        const timeoutId = setTimeout(() => {
            console.log("Data change detected, triggering save...");
            saveData(currentData);
        }, 1000); // 1 second debounce

        return () => clearTimeout(timeoutId);
    }, [departments, staffList, shifts, schedule, pickupLocations, reservationSources, shuttleReservations, parkingReservations, archivedParkingReservations, weekStartDay, propertyName, parkingCapacity, workloadFactors, saveData, isLoading, isSaving, user, isFirebaseInitialized, isAdminsLoaded, adminUsers]);
    
    useEffect(() => {
        if (!isFirebaseInitialized) return;

        const auth = getFirebaseAuth();
        const authUnsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                console.log("Auth state changed: User is logged in", currentUser.uid, currentUser.email || "anonymous");
                if (currentUser.email === 'staff@gen-lang-client.com' || currentUser.isAnonymous) {
                    // Background user or anonymous user is now active
                    setIsAuthLoaded(true);
                    return;
                }
                setUser(currentUser);
                setIsAuthLoaded(true);
            } else {
                console.log("Auth state changed: No user, attempting background login...");
                setUser(null);
                // No user, try anonymous login as it's more reliable for background access
                try {
                    await signInAnonymously(auth);
                    console.log("Anonymous login successful");
                    // isAuthLoaded will be updated by the next onAuthStateChanged call
                } catch (e: any) {
                    // Suppress the error log for restricted operation or disabled provider
                    if (e.code === 'auth/admin-restricted-operation' || e.code === 'auth/operation-not-allowed') {
                        console.log("Anonymous auth disabled in Firebase Console, falling back to email login.");
                    } else {
                        console.error("Anonymous login failed:", e);
                    }
                    
                    try {
                        await signInWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!');
                        // isAuthLoaded will be updated by the next onAuthStateChanged call
                    } catch (emailErr: any) {
                        if (emailErr.code === 'auth/user-not-found' || emailErr.code === 'auth/invalid-credential') {
                            try {
                                await createUserWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!');
                            } catch (createErr: any) {
                                if (createErr.code === 'auth/operation-not-allowed') {
                                    console.warn("Firebase Email/Password Auth is disabled.");
                                    toast.error("Firebase Authentication is disabled.");
                                } else {
                                    console.warn("Failed to create background user:", createErr);
                                }
                                setIsAuthLoaded(true);
                            }
                        } else {
                            if (emailErr.code === 'auth/operation-not-allowed') {
                                console.warn("Firebase Email/Password Auth is disabled.");
                            } else {
                                console.error("Background login failed:", emailErr);
                            }
                            setIsAuthLoaded(true);
                        }
                    }
                }
            }
        });

        return () => {
            authUnsubscribe();
        };
    }, [isFirebaseInitialized]);

    useEffect(() => {
        if (!isFirebaseInitialized || !isAuthLoaded) return;

        const auth = getFirebaseAuth();
        
        // We want to subscribe to core data only if we have an authenticated Firebase user
        // (including the background staff user).
        if (!auth.currentUser) return;
        const unsubscribeSchedule = subscribeToPath('scheduleData/schedule', (data) => {
            setSchedule(data || {});
            setIsDataLoaded(prev => ({ ...prev, schedule: true }));
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/schedule');
            }
        });

        const unsubscribeDepartments = subscribeToPath('scheduleData/departments', (data) => {
            const deps = data ? Object.values(data) : [];
            setDepartments(deps as Department[]);
            setIsDataLoaded(prev => ({ ...prev, departments: true }));
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/departments');
            }
        });

        const unsubscribeShifts = subscribeToPath('scheduleData/shifts', (data) => {
            const s = data ? Object.values(data) : [];
            setShifts(s as Shift[]);
            setIsDataLoaded(prev => ({ ...prev, shifts: true }));
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/shifts');
            }
        });

        const unsubscribePickupLocations = subscribeToPath('scheduleData/pickupLocations', (data) => {
            const locs = data ? Object.values(data) : [];
            setPickupLocations(locs as PickupLocation[]);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/pickupLocations');
            }
        });

        const unsubscribeReservationSources = subscribeToPath('scheduleData/reservationSources', (data) => {
            const srcs = data ? Object.values(data) : [];
            setReservationSources(srcs as ReservationSource[]);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/reservationSources');
            }
        });

        const unsubscribeStaffList = subscribeToPath('scheduleData/staffList', (data) => {
            const list = data ? Object.values(data) : [];
            setStaffList(list as StaffMember[]);
            setIsDataLoaded(prev => ({ ...prev, staffList: true }));
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData/staffList');
            }
        });

        const unsubscribeShuttleReservations = subscribeToPath('shuttleReservations', (data) => {
            const shuttle = data ? Object.values(data) as ShuttleReservation[] : [];
            setShuttleReservations(shuttle);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'shuttleReservations');
            }
        });

        const unsubscribeParkingReservations = subscribeToPath('parkingReservations', (data) => {
            const parking = data ? Object.values(data) as ParkingReservation[] : [];
            setParkingReservations(parking.filter(r => !r.archived));
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'parkingReservations');
            }
        });

        const unsubscribeArchivedParkingReservations = subscribeToPath('archivedParkingReservations', (data) => {
            const archived = data ? Object.values(data) as ParkingReservation[] : [];
            setArchivedParkingReservations(archived);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'archivedParkingReservations');
            } else {
                console.warn('Background user permission denied for /archivedParkingReservations');
            }
        });

        const unsubscribeGeneralSettings = subscribeToPath('scheduleData', (data) => {
            if (data) {
                if (data.weekStartDay !== undefined) setWeekStartDay(data.weekStartDay);
                if (data.propertyName !== undefined) setPropertyName(data.propertyName);
                if (data.parkingCapacity !== undefined) setParkingCapacity(data.parkingCapacity);
                if (data.showScheduleMetrics !== undefined) {
                    // Fallback to legacy global setting if needed, but per-user preference in AdminUser will take priority in its own useEffect
                }
                if (data.workloadFactors !== undefined) setWorkloadFactors(data.workloadFactors);
            }
            setIsDataLoaded(prev => ({ ...prev, settings: true }));
        }, (error) => {
            setIsDataLoaded(prev => ({ ...prev, settings: true }));
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'scheduleData');
            }
        });

        const unsubscribeTimeOff = subscribeToPath('timeOffRequests', (snapshot) => {
            const requestsArray: TimeOffRequest[] = [];
            if (snapshot) {
                Object.keys(snapshot).forEach(key => {
                    requestsArray.push({ id: key, ...snapshot[key] } as TimeOffRequest);
                });
            }
            setTimeOffRequests(prev => isEqual(prev, requestsArray) ? prev : requestsArray);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'timeOffRequests');
            }
        });

        const unsubscribeAdmins = subscribeToPath('users', (snapshot) => {
            if (snapshot) {
                const admins: AdminUser[] = [];
                Object.keys(snapshot).forEach(key => {
                    const data = snapshot[key];
                    const userObj = {
                        ...data,
                        id: key,
                        uid: key,
                        departmentIds: data.departmentIds || (data.departmentId ? [data.departmentId] : [])
                    };
                    
                    if (data.type === 'admin' || data.role === 'Super Admin' || data.role === 'Admin' || data.role === 'Administrator' || data.role === 'Manager') {
                        admins.push(userObj as AdminUser);
                    }
                });
                setAdminUsers(admins);
            }
            setIsAdminsLoaded(true);
            setFirebasePermissionError(false);
        }, (error) => {
            setIsAdminsLoaded(true);
            // Only log and handle error if it's not a background user permission issue
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'users');
            } else {
                console.warn('Background user permission denied for /users - this is expected if not an admin.');
            }
        });

        // Attendance records listener
        const unsubscribeAttendance = subscribeToPath('attendanceRecords', (snapshot) => {
            const list: AttendanceRecord[] = [];
            if (snapshot) {
                Object.keys(snapshot).forEach(key => {
                    list.push({
                        ...snapshot[key],
                        id: key} as AttendanceRecord);
                });
            }
            setAttendanceRecords(prev => isEqual(prev, list) ? prev : list);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'attendanceRecords');
            }
        });

        // Attendance requests listener
        const unsubscribeAttendanceRequests = subscribeToPath('attendanceRequests', (snapshot) => {
            const list: AttendanceRequest[] = [];
            if (snapshot) {
                Object.keys(snapshot).forEach(key => {
                    list.push({
                        ...snapshot[key],
                        id: key} as AttendanceRequest);
                });
            }
            setAttendanceRequests(prev => isEqual(prev, list) ? prev : list);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'attendanceRequests');
            }
        });

        const unsubscribeAttendanceArchives = subscribeToPath('attendanceArchives', (snapshot) => {
            const list: AttendanceArchive[] = [];
            if (snapshot) {
                Object.keys(snapshot).forEach(key => {
                    list.push({
                        ...snapshot[key],
                        id: key} as AttendanceArchive);
                });
            }
            setAttendanceArchives(prev => isEqual(prev, list) ? prev : list);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'attendanceArchives');
            }
        });

        const unsubscribeWeeklyMetrics = subscribeToPath('weeklyMetrics', (data) => {
            setWeeklyMetrics(data || {});
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'weeklyMetrics');
            }
        });

        const unsubscribeMaintenance = subscribeToPath('maintenanceRequests', (data) => {
            const list = data ? Object.values(data) as MaintenanceRequest[] : [];
            setMaintenanceRequests(list);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'maintenanceRequests');
            }
        });

        const unsubscribeNightAudit = subscribeToPath('dailyNightAuditReports', (data) => {
            const list = data ? Object.values(data) as DailyNightAuditReport[] : [];
            setNightAuditReports(list);
        }, (error) => {
            if (user?.email !== 'staff@gen-lang-client.com') {
                handleDatabaseError(error, OperationType.GET, 'dailyNightAuditReports');
            }
        });

        return () => {
            unsubscribeSchedule();
            unsubscribeDepartments();
            unsubscribeShifts();
            unsubscribePickupLocations();
            unsubscribeReservationSources();
            unsubscribeStaffList();
            unsubscribeShuttleReservations();
            unsubscribeParkingReservations();
            unsubscribeArchivedParkingReservations();
            unsubscribeGeneralSettings();
            unsubscribeTimeOff();
            unsubscribeAdmins();
            unsubscribeAttendance();
            unsubscribeAttendanceRequests();
            unsubscribeWeeklyMetrics();
            unsubscribeMaintenance();
            unsubscribeNightAudit();
        };
    }, [user, loggedInStaff, isFirebaseInitialized, isAuthLoaded]);
    
    useEffect(() => {
        const auth = getFirebaseAuth();
        if (!auth.currentUser) {
            setNotifications([]);
            return;
        }
        
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (!currentUserId || !isFirebaseInitialized) {
            setNotifications([]);
            return;
        }

        const unsubscribe = subscribeToNotifications(currentUserId, (newNotifications) => {
            setNotifications(newNotifications);
        });

        return () => unsubscribe();
    }, [user, loggedInStaff]);

    useEffect(() => {
        const savedStaffId = localStorage.getItem('scheduler_staff_id');
        
        if (staffList.length > 0) {
            if (loggedInStaff) {
                // Check if currently logged in staff was updated or deleted
                const updatedStaff = staffList.find(s => s.id === loggedInStaff.id);
                if (!updatedStaff || updatedStaff.status === 'deleted') {
                    setLoggedInStaff(null);
                    localStorage.removeItem('scheduler_staff_id');
                    toast.error('Your account has been removed or deactivated.');
                } else if (JSON.stringify(updatedStaff) !== JSON.stringify(loggedInStaff)) {
                    setLoggedInStaff(updatedStaff);
                }
            } else if (savedStaffId) {
                // Auto-login from localStorage
                const staff = staffList.find(s => s.id === savedStaffId && s.status !== 'deleted');
                if (staff) {
                    setLoggedInStaff(staff);
                    // Ensure they are authenticated with Firebase to bypass permission denied errors
                    const auth = getFirebaseAuth();
                    if (!auth.currentUser || (auth.currentUser.email !== 'staff@gen-lang-client.com' && !auth.currentUser.isAnonymous)) {
                        signInWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!').catch(authError => {
                            if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
                                createUserWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!').catch(e => console.warn(e));
                            }
                        });
                    }
                } else {
                    localStorage.removeItem('scheduler_staff_id');
                }
            }
        }
    }, [staffList, loggedInStaff]);
    
    // Check for parking reservations status updates and notify admins
    useEffect(() => {
        if (!user || parkingReservations.length === 0 || isLoading) return;

        const processParkingStatusUpdates = async () => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayStr = formatDateYYYYMMDD(today);
            
            let hasUpdates = false;
            const updatedReservations = [...parkingReservations];
            const reservationsToArchive: ParkingReservation[] = [];
            const isAdminUser = adminUsers.some(a => a.uid === user.uid);

            for (let i = 0; i < updatedReservations.length; i++) {
                const res = updatedReservations[i];
                let statusChanged = false;
                let notificationNeeded = false;
                let notificationTitle = '';
                let notificationMessage = '';
                let notificationLevel: 'info' | 'warning' | 'error' | 'success' = 'info';

                if (!res.startDate || !res.endDate) continue;

                const [startYear, startMonth, startDay] = res.startDate.split('-').map(Number);
                const startDate = new Date(startYear, startMonth - 1, startDay, 0, 0, 0, 0);
                
                const [endYear, endMonth, endDay] = res.endDate.split('-').map(Number);
                const endDate = new Date(endYear, endMonth - 1, endDay, 0, 0, 0, 0);

                // Check for archiving (inactive for 7 days)
                const daysSinceEnd = Math.floor((today.getTime() - endDate.getTime()) / (1000 * 60 * 60 * 24));
                const isExplicitlyInactive = res.status === 'Checkout' || res.status === 'Disactivated';
                
                // If explicitly inactive and 7 days have passed since end date, OR implicitly inactive (2 days) + 7 days = 9 days
                if ((isExplicitlyInactive && daysSinceEnd >= 7) || (!isExplicitlyInactive && daysSinceEnd >= 9)) {
                    reservationsToArchive.push({ ...updatedReservations[i], archivedAt: new Date().toISOString() } as any);
                    updatedReservations.splice(i, 1);
                    i--; // Adjust index after removal
                    hasUpdates = true;
                    continue; // Skip other checks for this archived reservation
                }

                // 1. No Show: After 23:00 of the arrival date (startDate)
                const arrivalDate2300 = new Date(startDate);
                arrivalDate2300.setHours(23, 0, 0, 0);
                
                if (res.status === 'Reservation' && new Date().getTime() >= arrivalDate2300.getTime()) {
                    updatedReservations[i] = { ...updatedReservations[i], status: 'No Show' };
                    statusChanged = true;
                    hasUpdates = true;
                }

                // 2. Disactivated: After one day of No Show
                // If it's at least 2 days after startDate and status is 'No Show'
                const twoDaysAfterStart = new Date(startDate);
                twoDaysAfterStart.setDate(startDate.getDate() + 2);
                
                if (updatedReservations[i].status === 'No Show' && today.getTime() >= twoDaysAfterStart.getTime()) {
                    updatedReservations[i] = { ...updatedReservations[i], status: 'Disactivated' };
                    statusChanged = true;
                    hasUpdates = true;
                }

                // 3. Status Transitions (Due Checkout / Late Checkout) based on endDate
                if (updatedReservations[i].status === 'Check-in' || updatedReservations[i].status === 'Due Checkout' || updatedReservations[i].status === 'Late Checkout') {
                    if (res.endDate === todayStr) {
                        if (updatedReservations[i].status !== 'Due Checkout') {
                            updatedReservations[i] = { ...updatedReservations[i], status: 'Due Checkout' };
                            statusChanged = true;
                            hasUpdates = true;
                        }
                    } else if (res.endDate < todayStr) {
                        if (updatedReservations[i].status !== 'Late Checkout') {
                            updatedReservations[i] = { ...updatedReservations[i], status: 'Late Checkout' };
                            statusChanged = true;
                            hasUpdates = true;
                        }
                    } else if (res.endDate > todayStr) {
                        // If it's in the future and somehow labelled as Due/Late, revert to Check-in
                        if (updatedReservations[i].status !== 'Check-in') {
                             updatedReservations[i] = { ...updatedReservations[i], status: 'Check-in' };
                             statusChanged = true;
                             hasUpdates = true;
                        }
                    }
                }

                // 4. Notifications for Late Checkout
                const currentStatus = updatedReservations[i].status;
                if (currentStatus === 'Late Checkout' && isAdminUser) {
                    const notified = updatedReservations[i].notifiedStatuses || [];
                    if (!notified.includes(currentStatus)) {
                        notificationNeeded = true;
                        notificationTitle = 'Late Checkout Alert';
                        notificationMessage = `Parking reservation #${res.confirmationNumber} for ${res.guestName} is now a Late Checkout. End date was ${res.endDate}.`;
                        notificationLevel = 'warning';
                        
                        updatedReservations[i] = { 
                            ...updatedReservations[i], 
                            notifiedStatuses: [...notified, currentStatus] 
                        };
                        hasUpdates = true;
                    }
                }

                if (notificationNeeded && isAdminUser) {
                    await createNotification(user.uid, {
                        title: notificationTitle,
                        message: notificationMessage,
                        level: notificationLevel,
                        recordId: res.id,
                        eventType: currentStatus
                    });
                }
            }

            if (hasUpdates) {
                setParkingReservations(updatedReservations);
                if (reservationsToArchive.length > 0) {
                    setArchivedParkingReservations(prev => [...prev, ...reservationsToArchive]);
                }
            }
        };

        processParkingStatusUpdates();
    }, [parkingReservations, user, adminUsers, isLoading]);

    const handleUpdateAttendanceByAdmin = useCallback(async (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, staffNote?: string, adminNote?: string }) => {
        if (!isFirebaseInitialized) return;
        
        try {
            const currentEntry = schedule[staffId]?.[date];
            if (!currentEntry || !currentEntry.shiftId) {
                toast.error("No shift assigned for this date.");
                return;
            }

            const baselineStart = currentEntry.assignedStartTime || '';
            const baselineEnd = currentEntry.assignedEndTime || '';
            
            if (!baselineStart || !baselineEnd) {
                // If snapshots are missing (e.g. legacy data), find shift template
                const shift = shifts.find(s => s.id === currentEntry.shiftId);
                if (shift) {
                    currentEntry.assignedStartTime = shift.startTime;
                    currentEntry.assignedEndTime = shift.endTime;
                }
            }

            const scheduledHours = calculateHours(currentEntry.assignedStartTime || '', currentEntry.assignedEndTime || '');
            const actualHours = calculateHours(updates.actualStartTime, updates.actualEndTime);
            
            const attendanceRecord: AttendanceRecord = {
                id: `${staffId}_${date}_${currentEntry.shiftId}`,
                employeeId: staffId,
                shiftId: currentEntry.shiftId,
                date: date,
                scheduledStartTime: currentEntry.assignedStartTime || '',
                scheduledEndTime: currentEntry.assignedEndTime || '',
                actualStartTime: updates.actualStartTime,
                actualEndTime: updates.actualEndTime,
                totalHours: actualHours,
                extraHours: Number((actualHours - scheduledHours).toFixed(2)),
                status: 'APPROVED',
                approvedAt: new Date().toISOString(),
                approvedBy: currentAdminUser?.name || user?.email || 'Admin',
                staffNote: updates.staffNote,
                adminNote: updates.adminNote
            };

            await database.saveAttendanceRecord(attendanceRecord);
            
            // Also update the schedule entry to be approved
            await database.updateScheduleEntry(staffId, date, {
                isApproved: true,
                isRejected: false,
                isTimeEdited: true,
                customStartTime: updates.actualStartTime,
                customEndTime: updates.actualEndTime
            });

            toast.success('Attendance updated and approved.');
        } catch (error: any) {
            console.error("Failed to update attendance:", error);
            toast.error("Failed to update attendance.");
        }
    }, [isFirebaseInitialized, schedule, shifts, user]);

    const handleRegisterFirstAdmin = async ({ name, email, password, phone }: LoginCredentials) => {
        if (adminUsers.length > 0 || !isFirebaseInitialized) {
            if (adminUsers.length > 0) toast.error("An administrator already exists. Please log in.");
            return;
        }
        setIsLoading(true);
        try {
            const normalizedPhone = normalizePhone(phone || '');
            const auth = getFirebaseAuth();
            let user;
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                user = userCredential.user;
            } catch (createError: any) {
                if (createError.code === 'auth/email-already-in-use') {
                    // If user exists, try to sign in instead
                    const userCredential = await signInWithEmailAndPassword(auth, email, password);
                    user = userCredential.user;
                } else {
                    throw createError;
                }
            }

            if (user) {
                await database.saveAdmin({
                    uid: user.uid,
                    name,
                    email,
                    role: 'Super Admin',
                    status: 'active',
                    createdAt: new Date().toISOString(),
                    allowedPages: ALL_PAGES,
                    color: '#3b82f6',
                    phone: normalizedPhone,
                    notes: '',
                    departmentIds: [],
                    availability: {
                        'Monday': 'Available', 'Tuesday': 'Available', 'Wednesday': 'Available',
                        'Thursday': 'Available', 'Friday': 'Available', 'Saturday': 'Available', 'Sunday': 'Available'
                    },
                    showInScheduler: false
                } as AdminUser);
                toast.success("First administrator registered successfully!");
            }
        } catch (error: any) {
            console.error("Registration Error:", error);
            toast.error(error.message || "Failed to register administrator.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickSetupFirstAdmin = async () => {
        if (adminUsers.length > 0 || !isFirebaseInitialized) {
            if (adminUsers.length > 0) toast.error("An administrator already exists. Please log in.");
            return;
        }
        setIsLoading(true);
        try {
            const auth = getFirebaseAuth();
            const email = 'admin@admin.com';
            const password = '4389258058';
            const name = 'System Administrator';
            
            let user;
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                user = userCredential.user;
            } catch (createError: any) {
                if (createError.code === 'auth/email-already-in-use') {
                    // If user exists, try to sign in instead
                    const userCredential = await signInWithEmailAndPassword(auth, email, password);
                    user = userCredential.user;
                } else {
                    throw createError;
                }
            }

            if (user) {
                await database.saveAdmin({
                    uid: user.uid,
                    name,
                    email,
                    role: 'Super Admin',
                    status: 'active',
                    createdAt: new Date().toISOString(),
                    allowedPages: ALL_PAGES,
                    color: '#3b82f6',
                    phone: '4389258058',
                    notes: 'Initial administrator created via quick setup.',
                    departmentIds: [],
                    availability: {
                        'Monday': 'Available', 'Tuesday': 'Available', 'Wednesday': 'Available',
                        'Thursday': 'Available', 'Friday': 'Available', 'Saturday': 'Available', 'Sunday': 'Available'
                    },
                    showInScheduler: false
                } as AdminUser);
                toast.success("First administrator registered successfully!");
            }
        } catch (error: any) {
            console.error("Quick Setup Error:", error);
            toast.error(error.message || "Failed to register administrator.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        if (!isFirebaseInitialized) return;
        const auth = getFirebaseAuth();
        const provider = new GoogleAuthProvider();
        try {
            const userCredential = await signInWithPopup(auth, provider);
            const user = userCredential.user;
            
            // If this is the first admin, automatically register them
            if (adminUsers.length === 0 && user) {
                await database.saveAdmin({
                    uid: user.uid,
                    name: user.displayName || 'Super Admin',
                    email: user.email || '',
                    role: 'Super Admin',
                    status: 'active',
                    createdAt: new Date().toISOString(),
                    allowedPages: ALL_PAGES,
                    color: '#3b82f6',
                    phone: '',
                    notes: '',
                    departmentIds: [],
                    availability: {
                        'Monday': 'Available', 'Tuesday': 'Available', 'Wednesday': 'Available',
                        'Thursday': 'Available', 'Friday': 'Available', 'Saturday': 'Available', 'Sunday': 'Available'
                    },
                    showInScheduler: false
                } as AdminUser);
                toast.success("First administrator registered successfully with Google!");
            } else {
                toast.success('Signed in with Google!');
            }
        } catch (error: any) {
            console.error("Google Login Error:", error);
            if (error.code === 'auth/popup-blocked') {
                toast.error('Popup blocked. Please allow popups for this site.');
            } else {
                toast.error('Google Sign-In failed. Please try again.');
            }
        }
    };

    const handleLogin = async (credentials: Omit<LoginCredentials, 'name'>, rememberMe: boolean) => {
        if (!isFirebaseInitialized) return;
        setIsLoading(true);
        let emailToUse = credentials.email;
        try {
            const auth = getFirebaseAuth();
            await setPersistence(auth, rememberMe ? browserLocalPersistence : browserSessionPersistence);
            
            // If phone is provided, look up the associated email
            if (credentials.phone && !credentials.email) {
                const userByPhone = await database.getUserByPhone(credentials.phone);
                if (!userByPhone) {
                    throw new Error("Phone number not found. Please check your entry or use email login.");
                }
                if (userByPhone.status !== 'active') {
                    throw new Error("This account is no longer active. Please contact an administrator.");
                }
                emailToUse = userByPhone.email;
            }

            if (!emailToUse) {
                throw new Error("Email or phone number is required for login.");
            }

            // Attempt Login via Firebase Auth
            await signInWithEmailAndPassword(auth, emailToUse, credentials.password);
            
            // If we get here, Firebase Auth was successful.
            // Now, verify the user is a valid admin in our database.
            const adminUser = auth.currentUser;
            if (!adminUser) {
                await signOut(auth);
                throw new Error("Login failed: User object not found after authentication.");
            }
            
            // Add a small delay to ensure auth state propagates to the database rules engine
            await new Promise(resolve => setTimeout(resolve, 200));
            
            let adminData;
            try {
                adminData = await database.getAdmin(adminUser.uid);
            } catch (dbError: any) {
                // Retry once if it's a permission error, as auth state might still be propagating
                if (dbError.code === 'permission-denied' || dbError.message?.toLowerCase().includes('permission denied')) {
                    await new Promise(resolve => setTimeout(resolve, 800));
                    adminData = await database.getAdmin(adminUser.uid);
                } else {
                    throw dbError;
                }
            }
            
            setFirebasePermissionError(false);

            if (adminData) {
                if (adminData.status === 'suspended') {
                    await signOut(auth);
                    toast.error('Your admin account has been suspended.');
                } else {
                    toast.success('Admin login successful!');
                }
            } else {
                // User authenticated with Firebase, but has no admin entry in our DB.
                // This is a permission error, not a credential error.
                await signOut(auth);
                toast.error('This account is not registered as an administrator.');
            }

        } catch (error: any) {
            const auth = getFirebaseAuth();
            // This catch block handles ALL failures from the `try` block above, including auth failures.
            
            // We only fall back to staff login if it was a credential-related auth error.
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
                
                // Try to find a matching staff member
                const staffMember = staffList.find(s => 
                    (s.email.toLowerCase() === (emailToUse || '').toLowerCase() || (credentials.phone && s.phone === normalizePhone(credentials.phone))) && 
                    s.password === credentials.password && 
                    s.status !== 'deleted'
                );
                
                if (staffMember) {
                    // Staff login successful
                    try {
                        await signInWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!');
                    } catch (authError: any) {
                        if (authError.code === 'auth/user-not-found' || authError.code === 'auth/invalid-credential') {
                            try {
                                await createUserWithEmailAndPassword(auth, 'staff@gen-lang-client.com', 'StaffPassword123!');
                            } catch (createError: any) {
                                if (createError.code === 'auth/operation-not-allowed') {
                                    console.warn("Firebase Email/Password Auth is disabled. Please enable it in the Firebase Console (Authentication > Sign-in method).");
                                    toast.error("Firebase Authentication (Email/Password) is disabled. Please enable it in the Firebase Console to allow staff access.");
                                } else {
                                    console.warn("Failed to create generic staff account:", createError);
                                }
                            }
                        } else if (authError.code === 'auth/operation-not-allowed') {
                            console.warn("Firebase Email/Password Auth is disabled. Please enable it in the Firebase Console (Authentication > Sign-in method).");
                            toast.error("Firebase Authentication (Email/Password) is disabled. Please enable it in the Firebase Console to allow staff access.");
                        } else {
                            console.warn("Failed to sign in with generic staff account:", authError);
                        }
                    }
                    setLoggedInStaff(staffMember);
                    if (rememberMe) {
                        localStorage.setItem('scheduler_staff_id', staffMember.id);
                    } else {
                        localStorage.removeItem('scheduler_staff_id');
                    }
                    toast.success(`Welcome, ${staffMember.name}!`);
                } else {
                    // Admin login failed AND staff login failed.
                    toast.error('Invalid credentials. Please check your email and password.');
                }
            } else {
                // A non-credential error occurred (e.g., network issue or permission denied)
                console.error("Login Error:", error);
                
                // If we get a permission denied here, it means the user is authenticated
                // but the database rules are blocking them from reading their own admin profile.
                if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                    // This is a serious configuration issue, so we show the error screen
                    setFirebasePermissionError(true);
                    toast.error('Permission denied. Your account may not have the required permissions.');
                } else {
                    toast.error(error.message || 'An unexpected login error occurred.');
                }
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleForgotPassword = async (email: string) => {
        if (!isFirebaseInitialized) return;
        if (!email) {
            toast.error('Please enter your email address in the email field first.');
            return;
        }
        setIsLoading(true);
        try {
            const auth = getFirebaseAuth();
            await sendPasswordResetEmail(auth, email);
            toast.success(`Password reset email sent to ${email}. Please check your inbox.`);
        } catch (error: any) {
            console.error("Forgot Password Error:", error);
            toast.error(error.message || 'Failed to send password reset email.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        if (!isFirebaseInitialized) return;
        const auth = getFirebaseAuth();
        if (user) await signOut(auth);
        if (loggedInStaff) {
            setLoggedInStaff(null);
            localStorage.removeItem('scheduler_staff_id');
        }
        toast.success('Logged out successfully.');
    };

    const handleStaffUpdateBySelf = useCallback(async (staff: StaffMember) => {
        if (!isFirebaseInitialized) return;
        
        const normalizedPhone = normalizePhone(staff.phone);
        
        // Check for phone uniqueness
        const existingWithPhone = await database.getUserByPhone(normalizedPhone);
        const existingId = existingWithPhone ? ((existingWithPhone as any).uid || (existingWithPhone as any).id) : null;
        if (existingWithPhone && existingId !== staff.id) {
            toast.error(`The phone number ${normalizedPhone} is already assigned to another user (${existingWithPhone.name}).`);
            return;
        }

        const staffIndex = staffList.findIndex(s => s.id === staff.id);
        if (staffIndex === -1) {
            toast.error("Could not find your profile to update.");
            return;
        }

        try {
            const deptIds = Array.isArray(staff.departmentIds) ? staff.departmentIds : (typeof staff.departmentIds === 'string' ? [staff.departmentIds] : ((staff as any).departmentId ? [(staff as any).departmentId] : []));
            const staffToSave = { ...staff, phone: normalizedPhone, departmentIds: deptIds };
            
            await database.updateStaffMember(staff.id, staffToSave);
            
            // Updating state is handled by the RTDB subscription
            setLoggedInStaff(staffToSave);
            
            toast.success(`Your profile has been updated, ${staff.name.split(' ')[0]}.`);
    
            const title = "Profile Updated by Staff";
            const message = `${staff.name} just updated their profile. Review the changes to stay up-to-date.`;
            const notificationPromises = adminUsers.map(admin =>
                createNotification(admin.uid, { title, message, level: 'info', recordId: staff.id, eventType: 'profile_update' })
            );
            await Promise.all(notificationPromises);
        } catch (error: any) {
            console.error("Staff profile save error:", error);
            if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error('Permission denied: You do not have access to update this profile.');
            } else {
                toast.error('Failed to save your profile.');
            }
        }
    }, [adminUsers, staffList, isFirebaseInitialized]);
    
    const normalizePhone = (phone: string) => {
        // Basic normalization: remove all non-digits except leading +
        const cleaned = phone.replace(/[^\d+]/g, '');
        // If it doesn't start with +, and is 10 digits, assume US/Canada +1
        if (cleaned.length === 10 && !cleaned.startsWith('+')) {
            return `+1${cleaned}`;
        }
        return cleaned;
    };

    const handleStaffProfileUpdateByAdmin = useCallback(async (staff: StaffMember) => {
        if (!isFirebaseInitialized) return;
        
        const normalizedPhone = normalizePhone(staff.phone);
        
        // Check for phone uniqueness
        const existingWithPhone = await database.getUserByPhone(normalizedPhone);
        const existingId = existingWithPhone ? ((existingWithPhone as any).uid || (existingWithPhone as any).id) : null;
        if (existingWithPhone && existingId !== staff.id) {
            toast.error(`The phone number ${normalizedPhone} is already assigned to another user (${existingWithPhone.name}).`);
            return;
        }

        const isNewStaff = !staff.id;
        const deptIds = Array.isArray(staff.departmentIds) ? staff.departmentIds : (typeof staff.departmentIds === 'string' ? [staff.departmentIds] : ((staff as any).departmentId ? [(staff as any).departmentId] : []));
        const staffToSave = { 
            ...staff, 
            phone: normalizedPhone,
            id: isNewStaff ? `staff${Date.now()}` : staff.id, 
            status: 'active' as const,
            departmentIds: deptIds
        };

        const staffIndex = isNewStaff
            ? staffList.length
            : staffList.findIndex(s => s.id === staffToSave.id);

        if (staffIndex === -1 && !isNewStaff) {
            toast.error("Could not find staff member to update.");
            return;
        }

        try {
            // Updating state will trigger saveData via useEffect
            setStaffList(prev => {
                const newList = [...prev];
                newList[staffIndex] = staffToSave;
                return newList;
            });
            
            if (isNewStaff) {
                setSchedule(prev => ({ ...prev, [staffToSave.id]: {} }));
            }

            // Sync with adminUsers if this staff member is also an admin
            const matchingAdmin = adminUsers.find(a => a.email?.toLowerCase() === staffToSave.email.toLowerCase());
            if (matchingAdmin) {
                const updatedAdmin: AdminUser = {
                    ...matchingAdmin,
                    role: staffToSave.role as any,
                    name: staffToSave.name,
                    departmentIds: staffToSave.departmentIds
                };
                await database.saveAdmin(updatedAdmin);
            }
            
            toast.success(`Staff member ${staffToSave.name} saved.`);

            if (!isNewStaff) {
                const title = "Your Profile Was Updated";
                const message = "An administrator has updated your profile. Please log in to your portal to review the changes.";
                createNotification(staff.id, { title, message, level: 'info', recordId: staff.id, eventType: 'profile_update_admin' })
                    .catch(err => console.error(`Failed to notify staff ${staff.id} of profile update:`, err));
            }
        } catch (error: any) {
            console.error("Admin save staff error:", error);
            if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error("Permission denied: You do not have permission to manage staff.");
            } else {
                toast.error("Failed to save staff data.");
            }
        }
    }, [staffList, isFirebaseInitialized]);


    const handleDeleteStaff = useCallback(async (staffId: string) => {
        if (!isFirebaseInitialized) return;
        const staffIndex = staffList.findIndex(s => s.id === staffId);
        const staffToDelete = staffList[staffIndex];
        
        if (staffIndex > -1 && staffToDelete) {
            try {
                // Soft delete by setting status in local state, which triggers saveData
                setStaffList(prev => prev.map(s => s.id === staffId ? { ...s, status: 'deleted' } : s));
                toast.success(`Staff member ${staffToDelete.name} deleted.`);
            } catch (error: any) {
                console.error("Delete staff error:", error);
                if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                    toast.error("Permission denied: You do not have permission to delete staff.");
                } else {
                    toast.error("Failed to delete staff member.");
                }
                throw new Error("Failed to delete staff member.");
            }
        }
    }, [staffList, isFirebaseInitialized]);
    
    const handleSaveDepartment = useCallback((department: Omit<Department, 'id'> & { id?: string }) => {
        const newDepartment = department.id 
            ? { ...department, id: department.id } 
            : { ...department, id: `dep${Date.now()}`, order: departments.length };
        setDepartments(prev => department.id ? prev.map(d => d.id === newDepartment.id ? newDepartment : d) : [...prev, newDepartment as Department]);
        toast.success(`Department "${newDepartment.name}" ${department.id ? 'updated' : 'added'}.`);
    }, [departments.length]);

    // Handle per-user settings persistence (Labor Factors Visibility)
    useEffect(() => {
        if (!user || !isAdminsLoaded) return;
        const currentAdmin = adminUsers.find(a => a.uid === user.uid);
        if (currentAdmin?.settings?.general?.showLaborFactors !== undefined) {
            setShowLaborFactors(currentAdmin.settings.general.showLaborFactors);
        }
    }, [user, adminUsers, isAdminsLoaded]);

    const handleUpdateShowLaborFactors = useCallback(async (value: boolean) => {
        setShowLaborFactors(value);
        if (user) {
            try {
                // Update in database immediately using slash notation for nested update
                await database.updateAdmin(user.uid, {
                    "settings/general/showLaborFactors": value
                } as any);
            } catch (error) {
                console.error("Failed to update showLaborFactors preference:", error);
                toast.error("Failed to save labor factors visibility preference.");
            }
        }
    }, [user]);

    const handleUpdateDepartmentsOrder = useCallback((updatedDepartments: Department[]) => {
        setDepartments(updatedDepartments);
        toast.success('Department order updated.');
    }, []);

    const handleDeleteDepartment = useCallback((departmentId: string) => {
        const departmentToDelete = departments.find(d => d.id === departmentId);
        if (departmentToDelete) {
            setDepartments(prev => prev.filter(d => d.id !== departmentId));
            setStaffList(prev => prev.map(staff => {
                const currentIds = Array.isArray(staff.departmentIds) ? staff.departmentIds : (typeof staff.departmentIds === 'string' ? [staff.departmentIds] : ((staff as any).departmentId ? [(staff as any).departmentId] : []));
                return {
                    ...staff,
                    departmentIds: currentIds.filter(id => id !== departmentId)
                };
            }));
        }
    }, [departments]);
    
    const handleReviewAttendanceRequest = useCallback(async (requestId: string, status: 'APPROVED' | 'REJECTED', adminNote?: string) => {
        if (!isFirebaseInitialized) return;
        try {
            const request = attendanceRequests.find(r => r.id === requestId);
            if (!request) {
                toast.error("Request not found.");
                return;
            }

            if (status === 'APPROVED') {
                const staffEntry = schedule[request.employeeId]?.[request.date];
                const baselineStart = staffEntry?.assignedStartTime || request.originalStartTime;
                const baselineEnd = staffEntry?.assignedEndTime || request.originalEndTime;
                const scheduledHours = calculateHours(baselineStart, baselineEnd);
                const actualHours = calculateHours(request.requestedStartTime, request.requestedEndTime);

                const attendanceRecord: AttendanceRecord = {
                    id: `${request.employeeId}_${request.date}_${request.shiftId}`,
                    employeeId: request.employeeId,
                    shiftId: request.shiftId,
                    date: request.date,
                    scheduledStartTime: baselineStart,
                    scheduledEndTime: baselineEnd,
                    actualStartTime: request.requestedStartTime,
                    actualEndTime: request.requestedEndTime,
                    totalHours: actualHours,
                    extraHours: Number((actualHours - scheduledHours).toFixed(2)),
                    status: 'APPROVED',
                    approvedAt: new Date().toISOString(),
                    approvedBy: currentAdminUser?.name || user?.email || 'Admin',
                    staffNote: request.staffNote,
                    adminNote: adminNote || request.adminNote
                };

                await database.saveAttendanceRecord(attendanceRecord);
                
                // Update schedule entry
                await database.updateScheduleEntry(request.employeeId, request.date, {
                    isApproved: true,
                    isRejected: false,
                    isTimeEdited: true,
                    customStartTime: request.requestedStartTime,
                    customEndTime: request.requestedEndTime
                });
            } else if (status === 'REJECTED') {
                 // Update schedule entry to show rejected
                 await database.updateScheduleEntry(request.employeeId, request.date, {
                    isApproved: false,
                    isRejected: true
                });
            }

            await database.updateAttendanceRequest(requestId, {
                status,
                adminNote,
                reviewedBy: currentAdminUser?.name || user?.email || 'Admin',
                reviewedAt: new Date().toISOString()
            });

            toast.success(`Attendance request ${status.toLowerCase()} successfully.`);
        } catch (error) {
            console.error("Failed to review attendance request:", error);
            toast.error("Failed to review request.");
        }
    }, [isFirebaseInitialized, attendanceRequests, shifts, user, attendanceRecords]);

    const handleArchivePayroll = useCallback(async () => {
        if (!isFirebaseInitialized || !isSuperAdmin) return;
        
        const processedRequests = attendanceRequests.filter(r => r.status !== 'PENDING' && r.id);
        if (processedRequests.length === 0) {
            toast.error("No processed requests to archive.");
            return;
        }

        try {
            const archives: Omit<AttendanceArchive, 'id'>[] = processedRequests.map(req => ({
                employeeId: req.employeeId,
                date: req.date,
                originalTimeIn: req.originalStartTime,
                originalTimeOut: req.originalEndTime,
                requestedTimeIn: req.requestedStartTime,
                requestedTimeOut: req.requestedEndTime,
                finalStatus: req.status as 'APPROVED' | 'REJECTED',
                adminNote: req.adminNote,
                archivedAt: new Date().toISOString(),
                archivedBy: currentAdminUser?.name || user?.email || 'Admin'
            }));

            await database.archiveAttendanceRequests(archives, processedRequests.map(r => r.id!));
            
            // Also archive finalized attendance records for that period? 
            // Requirement says "Move to Attendance Archive: Locks data for processed period".
            // Implementation of archiveAttendanceRequests already deletes the original requests.

            toast.success(`${processedRequests.length} records moved to archive. Payroll closed.`);
        } catch (error) {
            console.error("Failed to archive payroll:", error);
            toast.error("Failed to close payroll period.");
        }
    }, [isFirebaseInitialized, isSuperAdmin, attendanceRequests, currentAdminUser, user]);
    const handleSaveShift = useCallback((shift: Omit<Shift, 'id'> & { id?: string }) => {
        const newShift = shift.id ? { ...shift, id: shift.id } : { ...shift, id: `shift${Date.now()}` };
        setShifts(prev => shift.id ? prev.map(s => s.id === newShift.id ? newShift : s) : [...prev, newShift]);
        toast.success(`Shift "${newShift.name}" ${shift.id ? 'updated' : 'added'}.`);
    }, []);
    
    const handleSavePickupLocation = useCallback((location: Omit<PickupLocation, 'id'> & { id?: string }) => {
        const newLocation = location.id ? { ...location, id: location.id } : { ...location, id: `loc${Date.now()}` };
        setPickupLocations(prev => location.id ? prev.map(l => l.id === newLocation.id ? newLocation : l) : [...prev, newLocation]);
        toast.success(`Location "${newLocation.name}" ${location.id ? 'updated' : 'added'}.`);
    }, []);

    const handleDeletePickupLocation = useCallback((locationId: string) => {
        const locationToDelete = pickupLocations.find(l => l.id === locationId);
        if (locationToDelete) {
            setPickupLocations(prev => prev.filter(l => l.id !== locationId));
        }
    }, [pickupLocations]);

    const handleSaveReservationSource = useCallback((source: Omit<ReservationSource, 'id'> & { id?: string }) => {
        const newSource = source.id ? { ...source, id: source.id } : { ...source, id: `src${Date.now()}` };
        setReservationSources(prev => source.id ? prev.map(s => s.id === newSource.id ? newSource : s) : [...prev, newSource]);
        toast.success(`Source "${newSource.name}" ${source.id ? 'updated' : 'added'}.`);
    }, []);

    const handleDeleteReservationSource = useCallback((sourceId: string) => {
        const sourceToDelete = reservationSources.find(s => s.id === sourceId);
        if (sourceToDelete) {
            setReservationSources(prev => prev.filter(s => s.id !== sourceId));
        }
    }, [reservationSources]);

    const handleSaveShuttleReservation = useCallback((reservation: Omit<ShuttleReservation, 'id'> & { id?: string }) => {
        const cleanedRoomNumber = (reservation.roomNumber || '').toString().replace(/^R/i, '');
        const cleanedReservation = { ...reservation, roomNumber: cleanedRoomNumber };

        setShuttleReservations(prev => {
            const exists = cleanedReservation.id && prev.some(r => r.id === cleanedReservation.id);
            if (exists) {
                return prev.map(r => r.id === cleanedReservation.id ? { ...r, ...cleanedReservation } as ShuttleReservation : r);
            }
            const newReservation = { ...cleanedReservation, id: cleanedReservation.id || `res${Date.now()}` } as ShuttleReservation;
            return [...prev, newReservation];
        });
        toast.success(`Reservation for ${cleanedRoomNumber} ${reservation.id ? 'updated' : 'confirmed'}.`);
    }, []);

    const handleDeleteShuttleReservation = useCallback((reservationId: string) => {
        setShuttleReservations(prev => prev.filter(r => r.id !== reservationId));
        toast.success('Reservation deleted.');
    }, []);

    const handleDeleteShift = useCallback((shiftId: string) => {
        const shiftToDelete = shifts.find(s => s.id === shiftId);
        if (shiftToDelete) {
            setShifts(prev => prev.filter(s => s.id !== shiftId));
            setSchedule(prev => {
                const newSchedule = JSON.parse(JSON.stringify(prev));
                Object.values(newSchedule).forEach((staffSchedule: any) => {
                    Object.keys(staffSchedule).forEach(date => {
                        if (staffSchedule[date].shiftId === shiftId) {
                            staffSchedule[date] = { ...staffSchedule[date], shiftId: null, status: 'UNASSIGNED' };
                        }
                    });
                });
                return newSchedule;
            });
        }
    }, [shifts]);

    const handleCreateAdmin = async (credentials: LoginCredentials) => {
        if (!isFirebaseInitialized) return;
        
        const normalizedPhone = normalizePhone(credentials.phone || '');
        if (!normalizedPhone) {
            toast.error("Phone number is required.");
            return;
        }

        // Check for phone uniqueness
        const existingWithPhone = await database.getUserByPhone(normalizedPhone);
        if (existingWithPhone) {
            toast.error(`The phone number ${normalizedPhone} is already assigned to another user (${existingWithPhone.name}).`);
            return;
        }

        try {
            const auth = getFirebaseAuth();
            const userCredential = await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
            const newUser = userCredential.user;
            if (!newUser) throw new Error("Admin creation failed.");
            const newAdmin: AdminUser = {
                uid: newUser.uid,
                email: newUser.email || credentials.email,
                status: 'active',
                type: 'admin',
                name: credentials.name || 'New Admin',
                color: '#8b5cf6', 
                phone: normalizedPhone, 
                role: 'Admin', 
                notes: '', 
                departmentIds: [],
                availability: defaultAvailability,
                availabilityOverrides: {},
                showInScheduler: true,
                allowedPages: ALL_PAGES,
                createdAt: new Date().toISOString()
            };
            await database.saveAdmin(newAdmin);
            toast.success(`Admin user ${credentials.email} created successfully.`);
        } catch (error: any) {
            toast.error(error.message || 'Failed to create admin.');
            throw error;
        }
    };

    const handleSaveAdmin = async (adminData: AdminUser) => {
        if (!isFirebaseInitialized) return;
        
        const normalizedPhone = normalizePhone(adminData.phone);
        
        // Check for phone uniqueness
        const existingWithPhone = await database.getUserByPhone(normalizedPhone);
        const existingId = existingWithPhone ? ((existingWithPhone as any).uid || (existingWithPhone as any).id) : null;
        if (existingWithPhone && existingId !== adminData.uid) {
            toast.error(`The phone number ${normalizedPhone} is already assigned to another user (${existingWithPhone.name}).`);
            return;
        }

        const { uid, ...dataToSave } = adminData;
        await database.updateAdmin(uid, { ...dataToSave, phone: normalizedPhone });
        toast.success(`Admin profile for ${adminData.name} updated.`);
    };
    
    const handleDeleteCurrentUser = async () => {
        if (!isFirebaseInitialized) return;
        const auth = getFirebaseAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
            try {
                await database.deleteAdmin(currentUser.uid);
                await deleteUser(currentUser);
            } catch (error: any) {
                console.error('Failed to delete current user account:', error);
                throw new Error(error.message || 'Failed to delete your account.');
            }
        } else {
            throw new Error("No user is currently signed in.");
        }
    };

    const handleUpdateAdminStatus = async (uid: string, status: 'active' | 'suspended') => {
        if (!isFirebaseInitialized) return;
        await database.updateAdmin(uid, { status });
        toast.success(`Admin user status updated to ${status}.`);
    };

    const handleDeleteAdmin = async (uid: string, email: string) => {
        if (!isFirebaseInitialized) return;
        try {
            await database.deleteAdmin(uid);
            toast.success(`Admin permissions revoked for ${email}.`);
        } catch (error: any) {
            console.error(`Failed to revoke admin permissions for ${email}:`, error);
            if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                toast.error("Permission denied: You do not have permission to manage other administrators.");
            } else {
                toast.error('Failed to revoke admin permissions.');
            }
            throw new Error('Failed to revoke admin permissions.');
        }
    };
    
    const schedulablePeople = useMemo(() => {
        const activeStaff = staffList.filter(s => s.status !== 'deleted');
        const schedulableAdmins = adminUsers
            .filter(admin => admin.showInScheduler)
            .map(admin => ({ ...admin, id: admin.uid }));
        return [...activeStaff, ...schedulableAdmins];
    }, [staffList, adminUsers]);

    const handleScheduleUpdateByAdmin = useCallback((staffId: string, date: string, shiftId: string | null, status: AssignmentStatus, isForced?: boolean, departmentId?: string | null, customUpdates?: Partial<ScheduleEntry>) => {
        if (!isFirebaseInitialized) return;

        // Find shift template if applicable
        const shiftTemplate = shiftId ? shifts.find(s => s.id === shiftId) : null;

        setSchedule(prev => {
            const staffSchedule = prev[staffId] || {};
            const existingEntry = staffSchedule[date] || { staffId, shiftId, status };
            
            const newEntry: ScheduleEntry = { 
                ...existingEntry,
                staffId, 
                shiftId, 
                status, 
                isForced: isForced !== undefined ? isForced : (existingEntry.isForced || false), 
                departmentId: departmentId !== undefined ? departmentId : (existingEntry.departmentId || null),
                ...customUpdates
            };

            // REQUIREMENT: Store assigned times as baseline (decoupling)
            if (status === 'ASSIGNED' && shiftTemplate) {
                newEntry.assignedStartTime = shiftTemplate.startTime;
                newEntry.assignedEndTime = shiftTemplate.endTime;
                newEntry.shiftNameSnapshot = shiftTemplate.name;
                newEntry.shiftColorSnapshot = shiftTemplate.color;
            } else if (status !== 'ASSIGNED') {
                // Clear times if not assigned
                newEntry.assignedStartTime = undefined;
                newEntry.assignedEndTime = undefined;
                newEntry.shiftNameSnapshot = undefined;
                newEntry.shiftColorSnapshot = undefined;
                newEntry.customStartTime = undefined;
                newEntry.customEndTime = undefined;
                newEntry.isTimeEdited = false;
                newEntry.isApproved = false;
                newEntry.isRejected = false;
            }

            return {
                ...prev,
                [staffId]: {
                    ...staffSchedule,
                    [date]: newEntry
                }
            };
        });
    
        // Notify staff member (but not admins) of manual schedule changes.
        const person = schedulablePeople.find(p => p.id === staffId);
        const isStaff = staffList.some(s => s.id === staffId);
        
        if (person && isStaff) {
            let title = "Schedule Update";
            let message = `Your schedule for ${date} has been changed by an administrator.`;

            if (status === 'ASSIGNED' && shiftId) {
                const assignedShift = shifts.find(s => s.id === shiftId);
                if (assignedShift) {
                    title = "New Shift Assigned!";
                    message = `You've been assigned to the "${assignedShift.name}" shift (${assignedShift.startTime} - ${assignedShift.endTime}) on ${date}.`;
                }
            } else {
                const statusLabel = status.replace('_', ' ').toLowerCase();
                message = `Your status for ${date} has been updated to "${statusLabel}".`;
            }

            createNotification(staffId, { title, message, level: 'info', recordId: `${date}_${shiftId || status}`, eventType: 'schedule_update' })
                .catch(err => console.error(`Failed to notify staff ${staffId} of schedule update:`, err));
        }
    }, [schedulablePeople, staffList, shifts]);

    const handleImportSchedule = useCallback((newScheduleData: Schedule) => {
        if (!isFirebaseInitialized) return;
        setSchedule(prev => {
            const updatedSchedule = { ...prev };
            Object.keys(newScheduleData).forEach(staffId => {
                const staffSchedule = updatedSchedule[staffId] || {};
                updatedSchedule[staffId] = {
                    ...staffSchedule,
                    ...newScheduleData[staffId]
                };
            });
            return updatedSchedule;
        });
        toast.success("Schedule imported and updated.");
    }, [isFirebaseInitialized]);

    const handleMetricsUpdate = useCallback((date: string, field: string, value: number) => {
        database.updateWeeklyMetric(date, field, value);
    }, []);

    const normalizeTime = (time: string | undefined): string => {
        if (!time) return "";
        const parts = time.split(':');
        if (parts.length < 2) return time;
        const h = parts[0].padStart(2, '0');
        const m = parts[1].padStart(2, '0');
        return `${h}:${m}`;
    };

    const handleCreateAttendanceRequest = useCallback(async (staffId: string, date: string, updates: { requestedStartTime: string, requestedEndTime: string, staffNote?: string }) => {
        if (!isFirebaseInitialized) return;
        
        try {
            const currentEntry = schedule[staffId]?.[date];
            if (!currentEntry) {
                toast.error("No schedule entry found for this date.");
                return;
            }

            // Use assigned times from snapshots
            const baselineStart = currentEntry.assignedStartTime || '';
            const baselineEnd = currentEntry.assignedEndTime || '';

            if (!baselineStart || !baselineEnd) {
                // Fallback for legacy entries
                const shift = shifts.find(s => s.id === currentEntry.shiftId);
                if (shift) {
                    currentEntry.assignedStartTime = shift.startTime;
                    currentEntry.assignedEndTime = shift.endTime;
                } else {
                    toast.error("Missing original shift assignment data.");
                    return;
                }
            }

            // Normalize times to handle cases like "9:00" vs "09:00"
            const normRequestedStart = normalizeTime(updates.requestedStartTime);
            const normRequestedEnd = normalizeTime(updates.requestedEndTime);
            const normBaselineStart = normalizeTime(currentEntry.assignedStartTime || '');
            const normBaselineEnd = normalizeTime(currentEntry.assignedEndTime || '');

            const isMatch = normRequestedStart === normBaselineStart && normRequestedEnd === normBaselineEnd;

            if (isMatch) {
                // AUTO-APPROVE RULE
                const scheduledHours = calculateHours(currentEntry.assignedStartTime || '', currentEntry.assignedEndTime || '');
                const attendanceRecord: AttendanceRecord = {
                    id: `${staffId}_${date}_${currentEntry.shiftId || 'manual'}`,
                    employeeId: staffId,
                    shiftId: currentEntry.shiftId || null,
                    date: date,
                    scheduledStartTime: currentEntry.assignedStartTime || '',
                    scheduledEndTime: currentEntry.assignedEndTime || '',
                    actualStartTime: updates.requestedStartTime,
                    actualEndTime: updates.requestedEndTime,
                    totalHours: scheduledHours,
                    extraHours: 0,
                    status: 'APPROVED',
                    approvedAt: new Date().toISOString(),
                    approvedBy: 'SYSTEM_AUTO'
                };

                await database.saveAttendanceRecord(attendanceRecord);
                
                await database.updateScheduleEntry(staffId, date, {
                    isApproved: true,
                    isRejected: false,
                    isTimeEdited: false,
                    customStartTime: undefined,
                    customEndTime: undefined
                });

                // Create an auto-approved request record for tracking
                const attendanceRequest: Omit<AttendanceRequest, 'id'> = {
                    employeeId: staffId,
                    date: date,
                    shiftId: currentEntry.shiftId || null,
                    originalStartTime: currentEntry.assignedStartTime || '',
                    originalEndTime: currentEntry.assignedEndTime || '',
                    requestedStartTime: updates.requestedStartTime,
                    requestedEndTime: updates.requestedEndTime,
                    staffNote: updates.staffNote || 'Auto-confirmed (matches schedule)',
                    status: 'APPROVED',
                    autoApproved: true,
                    createdAt: new Date().toISOString(),
                    reviewedAt: new Date().toISOString(),
                    reviewedBy: 'SYSTEM_AUTO'
                };
                await database.createAttendanceRequest(attendanceRequest);

                toast.success('Attendance confirmed (matches schedule).');
            } else {
                // Create PENDING request
                const attendanceRequest: Omit<AttendanceRequest, 'id'> = {
                    employeeId: staffId,
                    date: date,
                    shiftId: currentEntry.shiftId || null,
                    originalStartTime: currentEntry.assignedStartTime || '',
                    originalEndTime: currentEntry.assignedEndTime || '',
                    requestedStartTime: updates.requestedStartTime,
                    requestedEndTime: updates.requestedEndTime,
                    staffNote: updates.staffNote || '',
                    status: 'PENDING',
                    createdAt: new Date().toISOString()
                };

                await database.createAttendanceRequest(attendanceRequest);
                
                // Mark schedule entry as having a pending change
                await database.updateScheduleEntry(staffId, date, {
                    isApproved: false,
                    isRejected: false,
                });

                toast.success('Attendance change request submitted for approval.');
            }
        } catch (error: any) {
            console.error("Failed to submit shift times:", error);
            toast.error("Failed to submit. Please try again.");
        }
    }, [isFirebaseInitialized, schedule, shifts]);

    const handleStaffPortalTimeUpdate = useCallback(async (staffId: string, date: string, updates: { actualStartTime: string, actualEndTime: string, reason: string }) => {
        await handleCreateAttendanceRequest(staffId, date, {
            requestedStartTime: updates.actualStartTime,
            requestedEndTime: updates.actualEndTime,
            staffNote: updates.reason
        });
    }, [handleCreateAttendanceRequest]);

    const handleRepeatSchedule = useCallback((sourceWeekStartDate: Date, repeatStartDate: Date, repeatEndDate: Date) => {
        const sourceWeekDates = getWeekDates(sourceWeekStartDate);
        const sourceScheduleByDay: { [dayIndex: number]: { [staffId: string]: ScheduleEntry } } = {};

        for (let i = 0; i < 7; i++) {
            const date = sourceWeekDates[i];
            const dateString = formatDateYYYYMMDD(date);
            sourceScheduleByDay[i] = {}; // 0 = Monday, 1 = Tuesday...
            for (const person of schedulablePeople) {
                const entry = schedule[person.id]?.[dateString];
                if (entry) {
                    sourceScheduleByDay[i][person.id] = entry;
                }
            }
        }

        const startDayIndex = DAY_INDEX_MAP[weekStartDay];
        let currentWeekStart = getWeekStartDate(repeatStartDate, startDayIndex);
        const end = new Date(repeatEndDate);

        setSchedule(prevSchedule => {
            const newSchedule = JSON.parse(JSON.stringify(prevSchedule));

            while (currentWeekStart <= end) {
                const targetWeekDates = getWeekDates(currentWeekStart);

                for (let i = 0; i < 7; i++) {
                    const targetDate = targetWeekDates[i];
                    if (targetDate > end) break; 

                    const targetDateString = formatDateYYYYMMDD(targetDate);
                    // FIX: Corrected variable from `sourceDaySchedule` to `sourceScheduleByDay` to prevent "used before declaration" error.
                    const sourceDaySchedule = sourceScheduleByDay[i];

                    for (const staffId in sourceDaySchedule) {
                        if (!newSchedule[staffId]) {
                            newSchedule[staffId] = {};
                        }
                        // FIX: Logic bug where an incorrect variable was being accessed.
                        newSchedule[staffId][targetDateString] = { ...sourceDaySchedule[staffId] };
                    }
                }
                const nextDate = new Date(currentWeekStart);
                nextDate.setDate(nextDate.getDate() + 7);
                currentWeekStart = nextDate;
            }
            return newSchedule;
        });

        toast.success('Schedule repeated successfully for the selected period!');
    }, [schedule, schedulablePeople, weekStartDay]);
    
    const handleMarkNotificationAsRead = (notificationId: string) => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            markNotificationAsRead(currentUserId, notificationId);
        }
    };

    const handleMarkAllNotificationsAsRead = () => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            markAllNotificationsAsRead(currentUserId, notifications);
        }
    };

    const handleClearNotifications = () => {
        if (!isFirebaseInitialized) return;
        const currentUserId = user?.uid || loggedInStaff?.id;
        if (currentUserId) {
            clearNotifications(currentUserId);
        }
    };

    const handleScheduleGenerated = async (weekStartDate: Date) => {
        if (!isFirebaseInitialized) return;
        const title = "New Schedule Is Live! 🗓️";
        const message = `Fresh off the press! The new schedule for the week of ${formatDateRange(weekStartDate)} is published and ready for you to view.`;
        
        const allUserIds = [
            ...staffList.map(s => s.id),
            ...adminUsers.map(a => a.uid),
        ];
        
        const uniqueUserIds = [...new Set(allUserIds)];

        const promises = uniqueUserIds.map(userId => 
            createNotification(userId, { title, message, level: 'success', recordId: formatDateYYYYMMDD(weekStartDate), eventType: 'schedule_published' })
        );
        
        try {
            await Promise.all(promises);
            toast.success("Notifications sent to all users.");
        } catch (error) {
            console.error("Failed to send notifications:", error);
            toast.error("Could not send notifications.");
        }
    };

    const handleCreateTimeOffRequest = useCallback(async (requestData: Omit<TimeOffRequest, 'id' | 'status' | 'createdAt' | 'staffName'>) => {
        if (!isFirebaseInitialized) return;
        const staffMember = staffList.find(s => s.id === requestData.employeeId);
        if (!staffMember) {
            toast.error("Could not find the staff profile to submit the request.");
            return;
        }
        
        // --- START VALIDATION ---
        if (new Date(requestData.endDate) < new Date(requestData.startDate)) {
            toast.error("Validation failed: End date cannot be before the start date.");
            return;
        }

        if (requestData.type === 'REPLACED') {
            const { replacementEmployeeId, employeeId, startDate, endDate } = requestData;

            if (!replacementEmployeeId) {
                toast.error("Validation failed: A replacement must be selected for this request type.");
                return;
            }

            const replacementStaff = staffList.find(s => s.id === replacementEmployeeId);
            if (!replacementStaff) {
                toast.error("Validation failed: Could not find the selected replacement staff member.");
                return;
            }

            if (replacementEmployeeId === employeeId) {
                toast.error("Validation failed: You cannot select yourself as a replacement.");
                return;
            }

            const getResolvedAvailability = (person: StaffMember, date: Date): AvailabilityStatus => {
                const dateString = formatDateYYYYMMDD(date);
                if (person.availabilityOverrides && person.availabilityOverrides[dateString]) {
                    return person.availabilityOverrides[dateString];
                }
                const dayOfWeek = getDayOfWeekFromDate(date);
                return (person.availability && person.availability[dayOfWeek]) || 'Available';
            };
            
            const parseDateAsLocal = (dateString: string) => {
                const [year, month, day] = dateString.split('-').map(Number);
                return new Date(year, month - 1, day);
            };

            let currentDate = parseDateAsLocal(startDate);
            const loopEndDate = parseDateAsLocal(endDate);

            while (currentDate <= loopEndDate) {
                const dateString = formatDateYYYYMMDD(currentDate);

                const availability = getResolvedAvailability(replacementStaff, currentDate);
                if (availability === 'Unavailable') {
                    toast.error(`Validation failed: ${replacementStaff.name} is unavailable on ${dateString}.`);
                    return;
                }

                const replacementSchedule = schedule[replacementStaff.id];
                const entry = replacementSchedule?.[dateString];
                const conflictingStatuses: AssignmentStatus[] = ['ASSIGNED', 'SICK', 'VACATION', 'ON_CALL', 'REFUSED'];
                if (entry && conflictingStatuses.includes(entry.status)) {
                    toast.error(`Validation failed: ${replacementStaff.name} already has an assignment on ${dateString}.`);
                    return;
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
        }
        // --- END VALIDATION ---
        
        try {
            const newStatus = requestData.type === 'REPLACED' && requestData.replacementEmployeeId ? 'PENDING_REPLACER' : 'PENDING_ADMIN';

            const newRequestData: Omit<TimeOffRequest, 'id'> = {
                ...requestData,
                userId: requestData.employeeId,
                staffName: staffMember.name,
                status: newStatus,
                createdAt: new Date().toISOString()
            };

            await database.createTimeOffRequest(newRequestData);

            // Notify Admins for ALL requests
            let adminTitle = "New Time Off Request";
            let adminMessage = `${staffMember.name} is requesting leave from ${requestData.startDate} to ${requestData.endDate}. Your approval is needed.`;
            if (requestData.type === 'REPLACED' && requestData.replacementEmployeeId) {
                const replacementStaff = staffList.find(s => s.id === requestData.replacementEmployeeId);
                adminTitle = "New Cover Request";
                adminMessage = `${staffMember.name} has requested time off and nominated ${replacementStaff?.name || 'a colleague'} to cover. Please review.`;
            }
            const adminNotificationPromises = adminUsers.map(admin =>
                createNotification(admin.uid, { title: adminTitle, message: adminMessage, level: 'info', recordId: `${requestData.employeeId}_${requestData.startDate}`, eventType: 'time_off_request_admin' })
            );
            
            // Notify Replacement Staff if applicable
            const replacementNotificationPromise = () => {
                if (newStatus === 'PENDING_REPLACER' && requestData.replacementEmployeeId) {
                    const title = "You've Been Asked to Cover a Shift!";
                    const message = `Teamwork! ${staffMember.name} has asked if you can cover their shifts from ${requestData.startDate} to ${requestData.endDate}. Please respond in your portal.`;
                    return createNotification(requestData.replacementEmployeeId, { title, message, level: 'info', recordId: `${requestData.employeeId}_${requestData.startDate}`, eventType: 'time_off_request_replacer' });
                }
                return Promise.resolve();
            };

            // Execute all promises
            await Promise.all([...adminNotificationPromises, replacementNotificationPromise()]);

        } catch (error: any) {
            console.error("Failed to create time off request:", error);
            toast.error("Could not submit the time off request. Please try again.");
            if (error.code === 'permission-denied' || error.message?.toLowerCase().includes('permission denied')) {
                setFirebasePermissionError(true);
            }
        }
    }, [staffList, adminUsers, schedule]);
    
    const handleReplacementDecision = useCallback(async (requestId: string, accepted: boolean) => {
        if (!isFirebaseInitialized) return;
        const request = timeOffRequests.find(r => r.id === requestId);
        if (!request || request.status !== 'PENDING_REPLACER') {
            toast.error("This request is no longer awaiting your response.");
            return;
        }

        const replacer = staffList.find(s => s.id === request.replacementEmployeeId);
        if (!replacer) return;

        try {
            const newStatus = accepted ? 'PENDING_ADMIN' : 'REJECTED';
            await database.updateTimeOffRequest(requestId, { status: newStatus });
                
            if (accepted) {
                const title = "Cover Request Accepted - Ready for Approval";
                const message = `${replacer.name} has agreed to cover for ${request.staffName}. The time off request is now awaiting your final decision.`;
                const notificationPromises = adminUsers.map(admin =>
                    createNotification(admin.uid, { title, message, level: 'info', recordId: requestId, eventType: 'time_off_replacer_accepted' })
                );
                await Promise.all(notificationPromises);
                toast.success("You've accepted the cover request. It has been sent for admin approval.");
            } else {
                const title = "Update on Your Time Off Request";
                const message = `Unfortunately, ${replacer.name} was unable to cover your shift from ${request.startDate} to ${request.endDate}. You may need to cancel this request or find an alternative.`;
                await createNotification(request.employeeId, { title, message, level: 'warning', recordId: requestId, eventType: 'time_off_replacer_rejected' });
                toast.success("You have rejected the cover request.");
            }
        } catch (error) {
            console.error("Failed to process replacement decision:", error);
            toast.error("Could not process your decision. Please try again.");
        }
    }, [timeOffRequests, staffList, adminUsers]);


    const handleUpdateTimeOffRequest = useCallback(async (requestId: string, data: Partial<TimeOffRequest>) => {
        if (!isFirebaseInitialized) return;
        try {
            await database.updateTimeOffRequest(requestId, data);
            toast.success("Time off request updated successfully.");
        } catch (error) {
            console.error("Failed to update time off request:", error);
            toast.error("Failed to update the request. Please try again.");
        }
    }, [isFirebaseInitialized]);

    const handleUpdateTimeOffRequestStatus = useCallback(async (requestId: string, status: 'APPROVED' | 'REJECTED', scheduleStatus: AssignmentStatus = 'VACATION') => {
        if (!isFirebaseInitialized) return;
        const request = timeOffRequests.find(r => r.id === requestId);
        if (!request) {
            toast.error("Could not find the request to update.");
            return;
        }
        
        if (request.status !== 'PENDING_ADMIN' && request.status !== 'PENDING_REPLACER') {
            toast.error("This request is not awaiting approval.");
            return;
        }

        const updatePromise = async () => {
            await database.updateTimeOffRequest(requestId, { status });
    
            if (status === 'APPROVED') {
                setSchedule(prev => {
                    const newSchedule = { ...prev };
                    let currentDate = new Date(request.startDate);
                    const endDate = new Date(request.endDate);

                    while (currentDate <= endDate) {
                        const dateString = formatDateYYYYMMDD(currentDate);

                        if (!newSchedule[request.employeeId]) newSchedule[request.employeeId] = {};
                        newSchedule[request.employeeId][dateString] = { staffId: request.employeeId, shiftId: null, status: scheduleStatus };

                        if (request.type === 'REPLACED' && request.replacementEmployeeId) {
                            const originalEntry = prev[request.employeeId]?.[dateString];
                            if (originalEntry && originalEntry.status === 'ASSIGNED' && originalEntry.shiftId) {
                                if (!newSchedule[request.replacementEmployeeId]) newSchedule[request.replacementEmployeeId] = {};
                                newSchedule[request.replacementEmployeeId][dateString] = { staffId: request.replacementEmployeeId, shiftId: originalEntry.shiftId, status: 'ASSIGNED' };
                            }
                        }
                        currentDate.setDate(currentDate.getDate() + 1);
                    }
                    return newSchedule;
                });
            }
            
            // Notification for original requester
            let title: string;
            let message: string;
            let level: 'success' | 'warning' = status === 'APPROVED' ? 'success' : 'warning';

            if (status === 'APPROVED') {
                title = "Time Off Approved! 🎉";
                message = `Great news! Your time off request for ${request.startDate} to ${request.endDate} has been approved. Enjoy your time off!`;
            } else { // REJECTED
                title = "Time Off Request Update";
                message = `Your time off request for ${request.startDate} to ${request.endDate} has been declined. Please see an administrator for more details.`;
            }
            await createNotification(request.employeeId, { title, message, level, recordId: requestId, eventType: `time_off_${status.toLowerCase()}` });
            
            // Notification for replacement staff
            if (status === 'APPROVED' && request.type === 'REPLACED' && request.replacementEmployeeId) {
                 const replacerTitle = "Cover Shift Confirmed ✅";
                 const replacerMessage = `The cover arrangement for ${request.staffName} (${request.startDate} - ${request.endDate}) is approved and has been added to your schedule. Thanks for your help!`;
                 await createNotification(request.replacementEmployeeId, { title: replacerTitle, message: replacerMessage, level: 'success', recordId: requestId, eventType: 'time_off_cover_confirmed' });
            }

            return `Request has been ${status.toLowerCase()}.`;
        };

        toast.promise(updatePromise(), {
            loading: 'Updating request status...',
            success: (message) => message,
            error: (err: any) => {
                console.error("Failed to update time off request:", err);
                return (err as Error).message || "Failed to update request status.";
            }
        });
    }, [timeOffRequests, schedule]);

    const handleDeleteTimeOffRequest = useCallback(async (requestId: string) => {
        if (!isFirebaseInitialized) return;
        const requestToDelete = timeOffRequests.find(r => r.id === requestId);
        if (!requestToDelete) {
            throw new Error("Request not found.");
        }

        const isAdmin = !!user;
        const isOwner = loggedInStaff?.id === requestToDelete.employeeId;
        const isPending = requestToDelete.status === 'PENDING_ADMIN' || requestToDelete.status === 'PENDING_REPLACER';

        if (isAdmin || (isOwner && isPending)) {
            try {
                await database.deleteTimeOffRequest(requestId);
            } catch(err: any) {
                if (err.code === 'permission-denied' || err.message?.toLowerCase().includes('permission denied')) {
                    setFirebasePermissionError(true);
                }
                throw new Error("Failed to delete request.");
            }
        } else {
            throw new Error("You don't have permission to delete this request.");
        }
    }, [user, loggedInStaff, timeOffRequests]);

    const handleImportParkingReservations = useCallback((newReservations: ParkingReservation[]) => {
        setParkingReservations(prev => {
            const updatedReservations = [...prev];
            newReservations.forEach(newRes => {
                const index = updatedReservations.findIndex(r => r.id === newRes.id || (newRes.confirmationNumber && r.confirmationNumber === newRes.confirmationNumber));
                if (index !== -1) {
                    updatedReservations[index] = { ...updatedReservations[index], ...newRes };
                } else {
                    updatedReservations.push(newRes);
                }
            });
            return updatedReservations;
        });
        toast.success(`Successfully processed ${newReservations.length} reservations`);
    }, []);

    const handleSaveParkingReservation = useCallback((reservation: Omit<ParkingReservation, 'id'> & { id?: string }) => {
        const cleanedRoomNumber = (reservation.roomNumber || '0').toString().replace(/^R/i, '');
        
        // Ensure counts are at least 1
        const safePeople = Math.max(1, parseInt(reservation.numberOfPeople || '0', 10) || 1).toString();
        const safeParkings = Math.max(1, parseInt(reservation.numberOfParkings || '0', 10) || 1).toString();
        const safeRooms = Math.max(1, parseInt(reservation.numberOfRooms || '0', 10) || 1).toString();

        const cleanedReservation = { 
            ...reservation, 
            roomNumber: cleanedRoomNumber,
            numberOfPeople: safePeople,
            numberOfParkings: safeParkings,
            numberOfRooms: safeRooms
        };

        setParkingReservations(prev => {
            const exists = cleanedReservation.id && prev.some(r => r.id === cleanedReservation.id);
            if (exists) {
                return prev.map(r => r.id === cleanedReservation.id ? { ...cleanedReservation, id: cleanedReservation.id } as ParkingReservation : r);
            }
            return [...prev, { ...cleanedReservation, id: cleanedReservation.id || Date.now().toString() } as ParkingReservation];
        });
        toast.success(reservation.id ? 'Parking reservation updated' : 'Parking reservation created');
    }, []);

    const handleDeleteParkingReservation = useCallback((id: string) => {
        setParkingReservations(prev => prev.filter(r => r.id !== id));
        toast.success('Parking reservation deleted');
    }, []);

    const handleInitiatePrint = useCallback((data: PrintData) => {
        setPrintData(data);
    }, []);

    const handleInitiateStaffWorkingHoursPrint = useCallback((data: any) => {
        setStaffWorkingHoursPrintData(data);
    }, []);

    const handleUpdateParkingStatus = useCallback((reservationId: string, status: ParkingReservation['status']) => {
        const today = new Date().toISOString().split('T')[0];
        setParkingReservations(prev => prev.map(r => r.id === reservationId ? { 
            ...r, 
            status, 
            updatedAt: new Date().toISOString(),
            hotelCheckInDate: status === 'Check-in' ? today : r.hotelCheckInDate,
            hotelCheckOutDate: status === 'Checkout' ? today : r.hotelCheckOutDate
        } : r));
    }, []);

    if (isLoading) return <LoadingSpinner />;

    if (staffWorkingHoursPrintData) {
        return <StaffWorkingHoursPrintPage {...staffWorkingHoursPrintData} onDone={() => setStaffWorkingHoursPrintData(null)} />;
    }
    
    if (printData) {
        return <PrintPage {...printData} onDone={() => setPrintData(null)} />;
    }
    
    if (shuttlePrintData) {
        return <ShuttlePrintPage {...shuttlePrintData} onDone={() => setShuttlePrintData(null)} />;
    }

    if (parkingPrintData) {
        return <ParkingPrintPage {...parkingPrintData} onDone={() => setParkingPrintData(null)} onUpdateStatus={handleUpdateParkingStatus} />;
    }

    if (bulkParkingPrintData) {
        return <BulkParkingPrintPage {...bulkParkingPrintData} onDone={() => setBulkParkingPrintData(null)} />;
    }

    if (attendancePrintData) {
        return <AttendancePrintPage {...attendancePrintData} records={attendanceRecords} onDone={() => setAttendancePrintData(null)} />;
    }

    if (payrollReportPrintData) {
        return <PayrollReportPrintPage {...payrollReportPrintData} onDone={() => setPayrollReportPrintData(null)} />;
    }

    if (staffWorkingHoursPrintData) {
        return <StaffWorkingHoursPrintPage {...staffWorkingHoursPrintData} onDone={() => setStaffWorkingHoursPrintData(null)} />;
    }

    const isLoggedIn = !!(user || loggedInStaff);
    const isAdmin = !!user;
    const isReadyToShowLogin = isAuthLoaded && isAdminsLoaded;

    if (isLoading || !isReadyToShowLogin) return <LoadingSpinner />;

    const renderContent = () => {
        const sortedDepartments = [...departments].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        if (user && !allowedPages.includes(view.page)) {
             return (
                <div className="flex flex-col items-center justify-center p-12 text-center bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-gray-200 dark:border-slate-700">
                    <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icons.ShieldAlert className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Restricted</h2>
                    <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                        You do not have permission to access the <span className="font-bold capitalize">{view.page}</span> page. Please contact a super administrator if you believe this is an error.
                    </p>
                    <button 
                        onClick={() => setView({ page: allowedPages[0] || 'scheduler' })}
                        className="mt-8 px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-all shadow-sm"
                    >
                        Go to {allowedPages[0] || 'Scheduler'}
                    </button>
                </div>
             );
        }
        if (firebaseInitError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-gray-50 dark:bg-slate-900">
                    <div className="p-8 bg-white rounded-2xl shadow-xl dark:bg-slate-800 max-w-md border border-red-100 dark:border-red-900/30">
                        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Configuration Error</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                            {firebaseInitError}
                        </p>
                        <div className="text-sm text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-slate-900/50 p-4 rounded-lg border border-gray-100 dark:border-slate-700 text-left">
                            <p className="font-semibold mb-2">How to fix this:</p>
                            <ol className="list-decimal ml-4 space-y-1">
                                <li>Ensure your <code className="bg-gray-200 dark:bg-slate-700 px-1 rounded">.env</code> file exists.</li>
                                <li>Check that all Firebase variables are correctly set.</li>
                                <li>Restart the development server if you just added the file.</li>
                            </ol>
                        </div>
                        <button 
                            onClick={() => window.location.reload()}
                            className="mt-8 w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-xl transition-colors shadow-lg shadow-indigo-500/30"
                        >
                            Retry Initialization
                        </button>
                    </div>
                </div>
            );
        }

        if (firebasePermissionError) return <PermissionErrorMessage />;

        if (!isLoggedIn) return <Login onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} onForgotPassword={handleForgotPassword} onRegisterFirstAdmin={handleRegisterFirstAdmin} onQuickSetupFirstAdmin={handleQuickSetupFirstAdmin} adminCount={adminUsers.length} propertyName={propertyName} />;
        if (loggedInStaff) {
             return <StaffPortal
                staffMember={loggedInStaff}
                shifts={shifts}
                schedule={schedule}
                weekStartDay={weekStartDay}
                departments={departments}
                allStaff={staffList.filter(s => s.status !== 'deleted')}
                onSaveStaff={handleStaffUpdateBySelf}
                onUpdateScheduleEntry={handleStaffPortalTimeUpdate}
                attendanceRecords={attendanceRecords}
                attendanceRequests={attendanceRequests}
                notifications={notifications}
                onMarkNotificationAsRead={handleMarkNotificationAsRead}
                onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                onClearNotifications={handleClearNotifications}
                timeOffRequests={timeOffRequests} // Pass all requests for cover functionality
                onCreateTimeOffRequest={handleCreateTimeOffRequest}
                onDeleteTimeOffRequest={handleDeleteTimeOffRequest}
                onReplacementDecision={handleReplacementDecision}
                shuttleReservations={shuttleReservations}
                pickupLocations={pickupLocations}
                onLogout={handleLogout}
                onSaveShuttleReservation={handleSaveShuttleReservation}
                onDeleteShuttleReservation={handleDeleteShuttleReservation}
                onInitiateShuttlePrint={(date) => setShuttlePrintData({ pickupLocations, reservations: shuttleReservations.filter(r => r.date === date), selectedDate: date, propertyName })}
                propertyName={propertyName}
                theme={theme}
                setTheme={setTheme}
            />;
        }
        if (isAdmin && user) {
            switch (view.page) {
                case 'scheduler': return <SchedulerView 
                    departments={sortedDepartments} 
                    people={schedulablePeople} 
                    shifts={shifts} 
                    schedule={schedule} 
                    weekStartDay={weekStartDay} 
                    setSchedule={setSchedule} 
                    setView={setView} 
                    onScheduleGenerated={handleScheduleGenerated} 
                    onCellUpdate={handleScheduleUpdateByAdmin} 
                    onImportSchedule={handleImportSchedule}
                    onRepeatSchedule={handleRepeatSchedule} 
                    onInitiatePrint={handleInitiatePrint}
                    weeklyMetrics={weeklyMetrics}
                    onMetricsUpdate={handleMetricsUpdate}
                    workloadFactors={workloadFactors}
                    attendanceRecords={attendanceRecords}
                    attendanceRequests={attendanceRequests}
                    onUpdateAttendance={handleUpdateAttendanceByAdmin}
                    onCreateAttendanceRequest={handleCreateAttendanceRequest}
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                    allowedPages={allowedPages}
                    showMetrics={showLaborFactors}
                />;
                case 'schedulerPreview': return <SchedulerPreviewView 
                    departments={sortedDepartments} 
                    people={schedulablePeople} 
                    shifts={shifts} 
                    schedule={schedule} 
                    weekStartDay={weekStartDay} 
                    onInitiatePrint={handleInitiatePrint}
                    workloadFactors={workloadFactors}
                    attendanceRecords={attendanceRecords}
                    attendanceRequests={attendanceRequests}
                    onUpdateAttendance={handleUpdateAttendanceByAdmin}
                    onCreateAttendanceRequest={handleCreateAttendanceRequest}
                    isAdmin={isAdmin}
                    isSuperAdmin={isSuperAdmin}
                    showMetrics={showLaborFactors}
                />;
                case 'staffList': return <StaffManagement staffList={staffList} departments={departments} weekStartDay={weekStartDay} setView={setView} onSaveStaff={handleStaffProfileUpdateByAdmin} onDeleteStaff={handleDeleteStaff} />;
                case 'timeOff': 
                    const role = currentAdminUser?.role?.toLowerCase();
                    const canAddDelete = role === 'super admin' || role === 'admin' || role === 'administrator';
                    const canEditApprove = role === 'super admin' || role === 'admin' || role === 'manager';
                    return <TimeOffManagementView 
                        requests={timeOffRequests} 
                        staffList={staffList} 
                        onUpdateRequestStatus={handleUpdateTimeOffRequestStatus} 
                        onDeleteRequest={handleDeleteTimeOffRequest} 
                        onCreateRequest={handleCreateTimeOffRequest} 
                        onUpdateRequest={handleUpdateTimeOffRequest}
                        canAddDelete={canAddDelete}
                        canEditApprove={canEditApprove}
                    />;
                case 'settings': return <SettingsView 
                    departments={departments} 
                    shifts={shifts} 
                    pickupLocations={pickupLocations} 
                    reservationSources={reservationSources}
                    weekStartDay={weekStartDay}
                    propertyName={propertyName}
                    parkingCapacity={parkingCapacity}
                    workloadFactors={workloadFactors}
                    onUpdateWorkloadFactors={setWorkloadFactors}
                    attendanceRequests={attendanceRequests}
                    onSaveDepartment={handleSaveDepartment} 
                    onDeleteDepartment={handleDeleteDepartment} 
                    onSaveShift={handleSaveShift} 
                    onDeleteShift={handleDeleteShift} 
                    onSavePickupLocation={handleSavePickupLocation} 
                    onDeletePickupLocation={handleDeletePickupLocation} 
                    onSaveReservationSource={handleSaveReservationSource}
                    onDeleteReservationSource={handleDeleteReservationSource}
                    onUpdateWeekStartDay={setWeekStartDay}
                    onUpdatePropertyName={setPropertyName}
                    onUpdateParkingCapacity={setParkingCapacity}
                    showLaborFactors={showLaborFactors}
                    onUpdateShowLaborFactors={handleUpdateShowLaborFactors}
                    onUpdateDepartmentsOrder={handleUpdateDepartmentsOrder}
                    currentUser={user}
                    adminUsers={adminUsers}
                    onCreateAdmin={handleCreateAdmin}
                    onDeleteCurrentUser={handleDeleteCurrentUser}
                    onUpdateAdminStatus={handleUpdateAdminStatus}
                    onDeleteAdmin={handleDeleteAdmin}
                    onSaveAdmin={handleSaveAdmin}
                    onReviewAttendanceRequest={handleReviewAttendanceRequest}
                    staffList={staffList}
                />;
                case 'shuttle': return <ShuttleReservationView pickupLocations={pickupLocations} reservations={shuttleReservations.filter(r => r.date === selectedDate)} onSaveReservation={(res) => handleSaveShuttleReservation({ ...res, date: selectedDate })} onDeleteReservation={handleDeleteShuttleReservation} selectedDate={selectedDate} onDateChange={setSelectedDate} onInitiatePrint={() => setShuttlePrintData({ pickupLocations, reservations: shuttleReservations.filter(r => r.date === selectedDate), selectedDate, propertyName })} />;
                case 'parking': return <ParkingReservationView 
                    reservations={parkingReservations} 
                    reservationSources={reservationSources} 
                    parkingCapacity={parkingCapacity} 
                    onSaveReservation={handleSaveParkingReservation} 
                    onImportReservations={handleImportParkingReservations} 
                    onDeleteReservation={handleDeleteParkingReservation} 
                    onInitiatePrint={(reservation) => setParkingPrintData({ reservation, propertyName })} 
                    onInitiateBulkPrint={(date) => setBulkParkingPrintData({ 
                        reservations: parkingReservations.filter(r => 
                            r.startDate === date && r.status === 'Reservation'
                        ), 
                        propertyName, 
                        selectedDate: date 
                    })} 
                />;
                case 'workforce': return <WorkforceTracker people={schedulablePeople} shifts={shifts} schedule={schedule} onBack={() => setView({ page: 'scheduler' })} />;
                case 'taskManager': return <TaskManagerView staffName={currentAdminUser?.name} staffList={staffList} isAdmin={isAdmin} />;
                case 'maintenance': return <MaintenanceView 
                    requests={maintenanceRequests}
                    staffName={currentAdminUser?.name || loggedInStaff?.name || 'Unknown'} 
                    staffList={staffList}
                    isAdmin={isAdmin} 
                />;
                case 'memos': return <MemosView isAdmin={isAdmin} staffId={currentAdminUser?.uid || user?.uid || ''} staffName={currentAdminUser?.name || user?.email || 'Admin'} />;
                case 'accessManager': return <AccessManagerView currentUser={currentAdminUser} />;
                case 'reports': return <ReportsView 
                    onInitiateStaffWorkingHoursPrint={handleInitiateStaffWorkingHoursPrint} 
                    onInitiatePayrollReport={(start, end, empId) => {
                        const filtered = empId 
                            ? attendanceRecords.filter(r => r.employeeId === empId)
                            : attendanceRecords;
                        setPayrollReportPrintData({
                            periodStart: start,
                            periodEnd: end,
                            records: filtered,
                            attendanceRequests: attendanceRequests.filter(req => req.status === 'APPROVED'), // Only count approved changes
                            staffList: staffList.filter(s => s.status !== 'deleted'),
                            shifts,
                            propertyName
                        });
                    }}
                    staffName={currentAdminUser?.name || loggedInStaff?.name || 'Unknown'}
                    adminUser={currentAdminUser}
                    staffList={staffList}
                    shifts={shifts}
                    schedule={schedule}
                    attendanceRecords={attendanceRecords}
                    attendanceRequests={attendanceRequests}
                    departments={departments}
                    propertyName={propertyName}
                    maintenanceRequests={maintenanceRequests}
                    parkingReservations={parkingReservations}
                    nightAuditReports={nightAuditReports}
                />;
                case 'attendance': return <AttendanceView 
                    staffList={staffList} 
                    schedule={schedule} 
                    shifts={shifts} 
                    attendanceRequests={attendanceRequests}
                    attendanceArchives={attendanceArchives}
                    onReviewRequest={handleReviewAttendanceRequest} 
                    onInitiatePrint={(start, end) => setAttendancePrintData({ periodStart: start, periodEnd: end, records: attendanceRecords, staffList })}
                    onArchivePayroll={handleArchivePayroll}
                />;
                case 'myProfile': return currentAdminUser ? <AdminProfileView adminUser={currentAdminUser} departments={departments} weekStartDay={weekStartDay} onSave={handleSaveAdmin} /> : <LoadingSpinner />;
                case 'help': return <HelpCenterView />;
                default: return <div>Page not found</div>;
            }
        }
        return <Login onLogin={handleLogin} onGoogleLogin={handleGoogleLogin} onForgotPassword={handleForgotPassword} onRegisterFirstAdmin={handleRegisterFirstAdmin} onQuickSetupFirstAdmin={handleQuickSetupFirstAdmin} adminCount={adminUsers.length} propertyName={propertyName} />;
    };
    
    return (
        <div className="min-h-screen bg-gray-50 text-gray-800 dark:bg-slate-900 dark:text-gray-200">
            <Toaster position="top-center" reverseOrder={false} toastOptions={{
                 style: isDarkMode ? {
                    border: '1px solid #4A5568',
                    padding: '16px',
                    color: '#F7FAFC',
                    backgroundColor: '#2D3748'} : {
                    border: '1px solid #E2E8F0',
                    padding: '16px',
                    color: '#1A202C',
                    backgroundColor: '#FFFFFF'},
                 success: {
                    iconTheme: {
                        primary: isDarkMode ? '#48BB78' : '#34D399',
                        secondary: '#F7FAFC'}},
                error: {
                    iconTheme: {
                        primary: isDarkMode ? '#F56565' : '#EF4444',
                        secondary: '#F7FAFC'}}}} />
            {isLoggedIn && !loggedInStaff && (
                <Header
                    isLoggedIn={isLoggedIn}
                    isAdmin={isAdmin}
                    userName={currentAdminUser?.name || user?.email || loggedInStaff?.name || ''}
                    onLogout={handleLogout}
                    view={view}
                    setView={handleSetView}
                    isSaving={isSaving}
                    notifications={notifications}
                    onMarkNotificationAsRead={handleMarkNotificationAsRead}
                    onMarkAllNotificationsAsRead={handleMarkAllNotificationsAsRead}
                    onClearNotifications={handleClearNotifications}
                    theme={theme}
                    setTheme={setTheme}
                    propertyName={propertyName}
                    allowedPages={allowedPages}
                />
            )}
            <main className={loggedInStaff ? "" : "p-4 sm:p-6 lg:p-8"}>{renderContent()}</main>
        </div>
    );
};

export default App;